/**
 * App Configuration for Dynamic Web Pages
 * 
 * This file contains metadata for all apps that have landing pages.
 * Each app has its own configuration for wallpapers, branding, and download links.
 * 
 * SOLID Principles:
 * - Single Responsibility: Each config object represents one app
 * - Open/Closed: Easy to add new apps without modifying existing code
 * - Dependency Inversion: Components depend on this abstraction, not concrete implementations
 * - DRY: CloudFront base URL defined once, reused everywhere
 */

// CDN Configuration - Single Source of Truth
const CDN_BASE_URL = "https://d272aay60jdo9x.cloudfront.net";

/**
 * Helper function to construct full CDN URL
 * @param path - Relative path from CDN root (e.g., "app-assets/logo.png")
 * @returns Full CloudFront URL
 */
const cdn = (path: string): string => `${CDN_BASE_URL}/${path}`;

export interface WallpaperConfig {
  id: number;
  image: string;
  category: string;
  title: string;
  likes: number;
}

export interface AppWebConfig {
  id: string; // Unique identifier for the app (used in URL)
  name: string; // App name displayed throughout the page
  tagline: string; // Hero section tagline
  description: string; // Short description
  
  // Branding
  logo: string; // App logo URL (CloudFront)
  primaryColor: string; // Primary brand color
  secondaryColor: string; // Secondary brand color
  gradientFrom: string; // Gradient start color
  gradientTo: string; // Gradient end color
  
  // Download Links
  playStoreUrl: string; // Google Play Store link
  appStoreUrl?: string; // Apple App Store link (optional)
  
  // Stats
  rating: string; // App rating (e.g., "4.8")
  downloads: string; // Download count (e.g., "1M+")
  totalWallpapers: string; // Total wallpapers (e.g., "10K+")
  
  // Hero Section Images - ALL images must be from CloudFront
  heroBackgroundImage: string; // Hero section background image URL
  heroImages: {
    iphone: string; // iPhone mockup wallpaper
    android: string; // Android mockup wallpaper
  };
  
  // Download Section Image
  downloadBackgroundImage: string; // Download section background image URL
  
  // Gallery Wallpapers (6 wallpapers to showcase)
  galleryWallpapers: WallpaperConfig[];
  
  // Legal Links
  legalLinks: {
    privacyPolicy: string;
    termsOfService: string;
  };
}

/**
 * Registry of all apps
 * Key: app identifier (lowercase, hyphenated)
 * Value: App configuration
 */
export const APPS_WEB_CONFIG: Record<string, AppWebConfig> = {
  "ganesha": {
    id: "ganesha",
    name: "Ganesha 4K Wallpapers",
    tagline: "Divine 4K HD Wallpapers",
    description: "Transform your device with stunning high-resolution wallpapers of Lord Ganesha. Browse thousands of premium 4K devotional images.",
    
    // Branding - Purple to Pink gradient
    logo: cdn("app-assets/ganesha-app/ganesha-logo.png"),
    primaryColor: "#AF52DE",
    secondaryColor: "#FF2D55",
    gradientFrom: "rgba(175, 82, 222, 0.9)",
    gradientTo: "rgba(255, 45, 85, 0.8)",
    
    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.ganesha",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.9",
    downloads: "500K+",
    totalWallpapers: "5K+",
    
    // Hero Section - ALL images from CloudFront (same for all apps)
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/ganesha/full/332d9292-a5d6-40b5-a3b1-65ec7d82c480.png"),
      android: cdn("wallpapers/ganesha/full/8fc92574-8d58-4eef-b640-4b42daa385ad.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/ganesha/thumbnails/045c2cf1-e13e-4a57-ac38-9055d5211381.jpg"),
        category: "Traditional",
        title: "Lord Ganesha Traditional Art",
        likes: 5847
      },
      {
        id: 2,
        image: cdn("wallpapers/ganesha/thumbnails/05cd714b-7385-49ef-8e37-8785c9b47593.png"),
        category: "Modern",
        title: "Modern Ganesha Design",
        likes: 4923
      },
      {
        id: 3,
        image: cdn("wallpapers/ganesha/thumbnails/0688019b-80bc-443a-bc99-f3e99a7ac1b2.png"),
        category: "Artistic",
        title: "Artistic Ganesha Portrait",
        likes: 6156
      },
      {
        id: 4,
        image: cdn("wallpapers/ganesha/thumbnails/08d11406-7307-4d40-98a6-b141a071a846.jpg"),
        category: "Festive",
        title: "Ganesh Chaturthi Special",
        likes: 7231
      },
      {
        id: 5,
        image: cdn("wallpapers/ganesha/thumbnails/0fd357be-7599-4742-9189-efeccfc9a0ab.png"),
        category: "Golden",
        title: "Golden Ganesha Idol",
        likes: 5674
      },
      {
        id: 6,
        image: cdn("wallpapers/ganesha/thumbnails/11706b9e-86f5-4c53-a1eb-206142b27843.png"),
        category: "Spiritual",
        title: "Spiritual Ganesha Meditation",
        likes: 4845
      }
    ],
    
    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=ganesha",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=ganesha",
    }
  },
  
  "instawall": {
    id: "instawall",
    name: "InstaWall",
    tagline: "Premium 4K HD Wallpapers",
    description: "Transform your device with stunning high-resolution wallpapers. Browse thousands of premium 4K images, like your favorites, and set them instantly.",
    
    // Branding - Original blue/purple gradient
    logo: cdn("app-assets/instwall-app/instawall-logo.png"),
    primaryColor: "#667eea",
    secondaryColor: "#764ba2",
    gradientFrom: "rgba(99, 102, 241, 0.9)",
    gradientTo: "rgba(168, 85, 247, 0.8)",
    
    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.instawall",
    appStoreUrl: "https://apps.apple.com/us/app/instawall-4k-wallpapers/id6759251745",
    
    // Stats
    rating: "4.8",
    downloads: "1M+",
    totalWallpapers: "10K+",
    
    // Hero Section - ALL images from CloudFront (same for all apps)
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/sky/full/416c0cba-bc0a-4d19-ad10-1f21c9c67ef3.png"),
      android: cdn("wallpapers/nature/full/5e674858-1336-4cd7-a219-1343dd66aba3.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/nature/full/6c15c509-a3d5-4258-a878-99da15abd378.png"),
        category: "Nature",
        title: "Mountain Sunrise Landscape",
        likes: 2847
      },
      {
        id: 2,
        image: cdn("wallpapers/urban/full/1200d2b0-7d50-4e52-b775-f65f02de5d3f.png"),
        category: "Urban",
        title: "City Skyline at Night",
        likes: 1923
      },
      {
        id: 3,
        image: cdn("wallpapers/nature/full/0f11ea8d-73e2-4401-a312-ae38ef42c408.png"),
        category: "Gradient",
        title: "Colorful Abstract Gradient",
        likes: 3156
      },
      {
        id: 4,
        image: cdn("wallpapers/beach/full/25730d8f-e90d-43de-a58e-ad97ab6f947b.png"),
        category: "Beach",
        title: "Tropical Beach Sunset",
        likes: 4231
      },
      {
        id: 5,
        image: cdn("wallpapers/cosmic/full/d43a4da5-3182-4d87-a3e0-0e08785c31d6.png"),
        category: "Cosmic",
        title: "Galaxy and Nebula Space",
        likes: 2674
      },
      {
        id: 6,
        image: cdn("wallpapers/christmas/full/86c5f5d4-ad14-484f-bb48-71df4fd85a66.png"),
        category: "Artistic",
        title: "Digital Art Creation",
        likes: 1845
      }
    ],
    
    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=instawall",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=instawall",
    }
  },

  "happyholi": {
    id: "happyholi",
    name: "Happy Holi 4K Wallpapers",
    tagline: "Vibrant Holi Festival 4K HD Wallpapers",
    description: "Celebrate the festival of colors with stunning high-resolution Holi wallpapers. Browse thousands of premium 4K festive images.",
    
    // Branding - Vibrant Holi colors (Pink to Orange gradient)
    logo: cdn("app-assets/happyholi-app/happyholi-logo.png"),
    primaryColor: "#FF6B6B",
    secondaryColor: "#FFA500",
    gradientFrom: "rgba(255, 107, 107, 0.9)",
    gradientTo: "rgba(255, 165, 0, 0.8)",
    
    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.happyholi",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/holi/thumbnails/2704ea62-2244-4f30-af28-2d9e9fe8a9c0.jpg"),
      android: cdn("wallpapers/holi/thumbnails/7dfedc17-173e-4a54-bcbf-40fd660230b5.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/holi/thumbnails/2704ea62-2244-4f30-af28-2d9e9fe8a9c0.jpg"),
        category: "Festival",
        title: "Colorful Holi Celebration",
        likes: 3847
      },
      {
        id: 2,
        image: cdn("wallpapers/holi/thumbnails/7dfedc17-173e-4a54-bcbf-40fd660230b5.jpg"),
        category: "Traditional",
        title: "Holi Colors Splash",
        likes: 4523
      },
      {
        id: 3,
        image: cdn("wallpapers/holi/thumbnails/c1361d5e-ec56-4a02-8a65-ab7af66bf6a5.jpg"),
        category: "Vibrant",
        title: "Vibrant Holi Colors",
        likes: 3956
      },
      {
        id: 4,
        image: cdn("wallpapers/holi/thumbnails/68f970bb-66c4-4414-9b98-2249621f90ed.jpg"),
        category: "Artistic",
        title: "Holi Festival Joy",
        likes: 4231
      },
      {
        id: 5,
        image: cdn("wallpapers/holi/thumbnails/91cd2091-431b-4ba6-babd-dc5b8c3464e6.jpg"),
        category: "Cultural",
        title: "Traditional Holi Festival",
        likes: 3674
      },
      {
        id: 6,
        image: cdn("wallpapers/holi/thumbnails/4aac4388-5a4f-4251-ab56-4dbb38a637ca.jpg"),
        category: "Modern",
        title: "Modern Holi Art",
        likes: 4092
      }
    ],
    
    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=happyholi",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=happyholi",
    }
  },

  "indianarmy": {
    id: "indianarmy",
    name: "Indian Army 4K Wallpapers",
    tagline: "Honor the Brave — Premium 4K HD Wallpapers",
    description: "Celebrate the Indian Armed Forces with stunning high-resolution wallpapers featuring soldiers, military vehicles, patriotic imagery and national pride in premium 4K quality.",

    // Branding - Military olive green to dark green gradient
    logo: cdn("app-assets/indianarmy-app/indianarmy-logo.png"),
    primaryColor: "#556B2F",
    secondaryColor: "#8B6914",
    gradientFrom: "rgba(85, 107, 47, 0.92)",
    gradientTo: "rgba(62, 80, 30, 0.88)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.indianarmy",
    appStoreUrl: undefined,

    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",

    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/indian-army/thumbnails/18741a47-358d-4845-adf0-201509ca511d.jpg"),
      android: cdn("wallpapers/indian-army/thumbnails/9421e1f3-f025-4818-94d7-10ad1a16da71.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),

    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/indian-army/thumbnails/0e14fce0-9763-4adc-9ce4-af2455fe4df9.jpg"),
        category: "Soldiers",
        title: "Indian Army Soldiers",
        likes: 6241
      },
      {
        id: 2,
        image: cdn("wallpapers/indian-army/thumbnails/72e70c3d-9d2c-400f-944a-178b5cedef6d.jpg"),
        category: "Patriotic",
        title: "Jai Hind — Patriotic Pride",
        likes: 5834
      },
      {
        id: 3,
        image: cdn("wallpapers/indian-army/thumbnails/9a41acf2-08f0-4bdf-ba77-a7affe3a2dff.jpg"),
        category: "Military",
        title: "Military Valor",
        likes: 7102
      },
      {
        id: 4,
        image: cdn("wallpapers/indian-army/thumbnails/d3028cb2-ce64-4b5f-b001-148568644018.jpg"),
        category: "Flag",
        title: "Tiranga — Indian Flag",
        likes: 8317
      },
      {
        id: 5,
        image: cdn("wallpapers/indian-army/thumbnails/04e5aa68-d19d-4412-b555-8b5174292599.jpg"),
        category: "Special Forces",
        title: "Special Forces Elite",
        likes: 5923
      },
      {
        id: 6,
        image: cdn("wallpapers/indian-army/thumbnails/d0c4fde4-3494-4a1a-ad97-2e21fdec6268.jpg"),
        category: "Tribute",
        title: "Salute to the Brave",
        likes: 6784
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=indianarmy",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=indianarmy",
    }
  },

  "nature": {
    id: "nature",
    name: "Nature 4K Wallpapers",
    tagline: "Breathtaking Nature 4K & HD Wallpapers",
    description: "Explore the beauty of nature with stunning high-resolution wallpapers. Discover mountains, forests, oceans, sunsets, wildlife and more in premium 4K quality.",
    
    // Branding - Green to Blue gradient
    logo: cdn("app-assets/nature-app/nature-logo.png"),
    primaryColor: "#2E8B57",
    secondaryColor: "#1E90FF",
    gradientFrom: "rgba(46, 139, 87, 0.9)",
    gradientTo: "rgba(30, 144, 255, 0.85)",
    
    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.nature",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/mountains/thumbnails/eb55ccb5-e869-4f6d-adc3-78334add3c38.jpg"),
      android: cdn("wallpapers/moon/thumbnails/0af3ffcf-4626-4ddc-8196-c397c891bea8.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/mountains/thumbnails/eb55ccb5-e869-4f6d-adc3-78334add3c38.jpg"),
        category: "Mountains",
        title: "Misty Mountain Sunrise",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/moon/thumbnails/0af3ffcf-4626-4ddc-8196-c397c891bea8.jpg"),
        category: "Forests",
        title: "Serene Forest Path",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/beach/thumbnails/7eecc66d-4bd6-4932-80c8-d2c1f0fadfcb.jpg"),
        category: "Oceans",
        title: "Crystal Blue Ocean Waves",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/autumn/thumbnails/36e7d14a-614f-46f8-aa75-be16b219a92a.jpg"),
        category: "Sunsets",
        title: "Golden Sunset Horizon",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/tiger/thumbnails/2c6daf05-c768-4df4-82bc-fca0fbe4b3e9.jpg"),
        category: "Wildlife",
        title: "Majestic Tiger in the Wild",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/nature/thumbnails/766c81e9-ec37-4b29-910a-08d0b3dc06d7.png"),
        category: "Waterfalls",
        title: "Hidden Waterfall Paradise",
        likes: 8796
      }
    ],
    
    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=nature",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=nature",
    }
  },

  "beach": {
    id: "beach",
    name: "Beach 4K Wallpapers",
    tagline: "Relaxing Beach & Ocean 4K Wallpapers",
    description: "Escape to tropical beaches with stunning high-resolution wallpapers. Discover crystal clear waters, golden sand, sunsets, palm trees and relaxing coastal scenery in premium 4K quality.",
    
    // Branding - Blue to Gold gradient
    logo: cdn("app-assets/beach-app/beach-logo.png"),
    primaryColor: "#00BFFF",
    secondaryColor: "#FFD700",
    gradientFrom: "rgba(0, 191, 255, 0.9)",
    gradientTo: "rgba(255, 215, 0, 0.85)",
    
    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.beach",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/beach/thumbnails/4147ab9a-3e32-48bc-b5ed-1f3ea400d5c1.jpg"),
      android: cdn("wallpapers/beach/thumbnails/72c67364-e924-4cdd-ae02-fa39f27116b8.jpg"),
    },

    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/beach/thumbnails/66ff1d25-2da7-4454-9462-cc0dddd2c705.jpg"),
        category: "Tropical",
        title: "Tropical Paradise Beach",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/beach/thumbnails/080b1f5b-6e54-4f2f-a6f9-57755931e9ca.jpg"),
        category: "Sunset",
        title: "Golden Beach Sunset",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/beach/thumbnails/72c67364-e924-4cdd-ae02-fa39f27116b8.jpg"),
        category: "Palm Trees",
        title: "Palm Tree Coastal View",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/beach/thumbnails/90ddc630-7104-4a91-b3d2-fe98ebaaf778.jpg"),
        category: "Islands",
        title: "Island Lagoon Beauty",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/beach/thumbnails/7eecc66d-4bd6-4932-80c8-d2c1f0fadfcb.jpg"),
        category: "Ocean",
        title: "Deep Blue Ocean Waves",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/cute-girl/thumbnails/908b109b-b797-4237-9f13-5eabeec30a15.png"),
        category: "Relax",
        title: "Peaceful Beach Morning",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=beach",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=beach",
    }
  },

  "ai": {
    id: "ai",
    name: "AI 4K Wallpapers",
    tagline: "Stunning AI Generated 4K Wallpapers",
    description: "Discover unique wallpapers created with artificial intelligence. Explore futuristic art, digital landscapes, abstract visuals and stunning AI creations in ultra high-resolution 4K quality.",
    
    // Branding - Purple to Cyan gradient
    logo: cdn("app-assets/ai-app/ai-logo.png"),
    primaryColor: "#7B68EE",
    secondaryColor: "#00FFFF",
    gradientFrom: "rgba(123, 104, 238, 0.9)",
    gradientTo: "rgba(0, 255, 255, 0.85)",
    
    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.ai",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/mountains/thumbnails/eb55ccb5-e869-4f6d-adc3-78334add3c38.jpg"),
      android: cdn("wallpapers/moon/thumbnails/0af3ffcf-4626-4ddc-8196-c397c891bea8.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/urban/thumbnails/5d823ad6-3e05-42f8-a3dc-271d56b234f2.jpg"),
        category: "Futuristic",
        title: "Cyberpunk AI City",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/abstract/thumbnails/248e39c6-6ed3-4537-8859-2ed9dd72273a.jpg"),
        category: "Abstract",
        title: "Neon Abstract Flow",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/fantasy/thumbnails/fb55b19e-71a9-4a1f-858a-8f787fb6cbe0.jpg"),
        category: "Fantasy",
        title: "Dreamscape AI World",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/cosmic/thumbnails/99d54a20-2e04-40fb-b845-2ce4192a7949.png"),
        category: "Space",
        title: "AI Cosmic Universe",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/anime/thumbnails/601a2641-3372-47fc-b9f1-06984c66f6cf.jpg"),
        category: "Digital Art",
        title: "Imaginative Designs",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/moon/thumbnails/f97e2ea9-b7d4-479d-8d72-59348b1ad21a.jpg"),
        category: "Sci-Fi",
        title: "AI Sci-Fi Landscape",
        likes: 8796
      }
    ],
    
    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=ai",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=ai",
    }
  },

  "animal": {
    id: "animal",
    name: "Animal 4K Wallpapers",
    tagline: "Beautiful Wildlife & Animal Wallpapers",
    description: "Discover stunning wildlife and animal wallpapers in high resolution. Explore lions, tigers, elephants, birds, marine animals and adorable creatures in breathtaking 4K quality.",
    
    // Branding - Brown to Green gradient
    logo: cdn("app-assets/animal-app/animal-logo.png"),
    primaryColor: "#8B4513",
    secondaryColor: "#228B22",
    gradientFrom: "rgba(139,69,19,0.9)",
    gradientTo: "rgba(34,139,34,0.85)",
    
    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.animal",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/krishna/thumbnails/8ac4d2e8-7bdd-4d11-9f7a-e2eb9e989dac.jpg"),
      android: cdn("wallpapers/tiger/thumbnails/2c6daf05-c768-4df4-82bc-fca0fbe4b3e9.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/tiger/thumbnails/f30bdff0-fb42-4873-9bb3-7421aa98a5e9.jpg"),
        category: "Wildlife",
        title: "Majestic Tiger",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/lion/thumbnails/5a24735f-f225-4ffd-916d-0c3b171256a9.jpg"),
        category: "Lions",
        title: "King of the Jungle",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/bird/thumbnails/3a260780-3b2a-4580-a48b-9e9ce08f5af5.jpg"),
        category: "Birds",
        title: "Colorful Tropical Bird",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/animal/thumbnails/deb0a164-a344-42a9-8894-3b854b2cd852.jpg"),
        category: "Elephants",
        title: "Gentle Giant Elephant",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/dolphin/thumbnails/8d7af526-2a2a-479c-b355-04dd39218592.jpg"),
        category: "Marine",
        title: "Ocean Dolphin Jump",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/panda/thumbnails/5f197f05-817a-4cec-83a8-ef4fae58e2f3.jpg"),
        category: "Cute",
        title: "Adorable Panda",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=animal",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=animal",
    }
  },

  "anime": {
    id: "anime",
    name: "Anime 4K Wallpapers",
    tagline: "Epic Anime HD & 4K Wallpapers",
    description: "Discover stunning anime wallpapers featuring popular characters, epic battles, fantasy worlds and beautiful anime art. Explore thousands of high-quality anime backgrounds in crisp 4K resolution.",
    
    // Branding - Pink to Red gradient
    logo: cdn("app-assets/anime-app/anime-logo.png"),
    primaryColor: "#FF69B4",
    secondaryColor: "#FF1493",
    gradientFrom: "rgba(255,105,180,0.9)",
    gradientTo: "rgba(255,20,147,0.85)",
    
    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.anime",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/anime/thumbnails/3fb15325-4e5d-495c-a237-5065d0fda8a5.jpg"),
      android: cdn("wallpapers/cute-girl/thumbnails/1fabb649-84cb-46c5-b71d-675b1ad3534d.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/anime/thumbnails/d8035dc8-b825-4461-8ef8-fb75fdefd1e5.png"),
        category: "Characters",
        title: "Anime Hero Portrait",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/anime/thumbnails/81ee26e8-3795-47c8-a69b-04835d683bb8.jpg"),
        category: "Fantasy",
        title: "Fantasy Anime World",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/anime/thumbnails/da8059e4-efc5-4982-b84e-7b2bdf98c77d.jpg"),
        category: "Action",
        title: "Epic Battle Scene",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/anime/thumbnails/1288270f-091f-4712-a530-6f936855d221.jpg"),
        category: "Aesthetic",
        title: "Neon Anime Night",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/anime/thumbnails/3fb15325-4e5d-495c-a237-5065d0fda8a5.jpg"),
        category: "Samurai",
        title: "Anime Samurai Warrior",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/christmas/thumbnails/c7207c56-a5ce-43ad-9be7-2ddd660403e6.png"),
        category: "Cute",
        title: "Cute Anime Girl",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=anime",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=anime",
    }
  },

  "autumn": {
    id: "autumn",
    name: "Autumn 4K Wallpapers",
    tagline: "Beautiful Autumn & Fall Wallpapers",
    description: "Experience the warm beauty of autumn with stunning fall wallpapers. Discover golden leaves, peaceful forests, cozy landscapes and colorful fall scenery in breathtaking 4K resolution.",
    
    // Branding - Orange to Red gradient
    logo: cdn("app-assets/autumn-app/autumn-logo.png"),
    primaryColor: "#FF8C00",
    secondaryColor: "#B22222",
    gradientFrom: "rgba(255, 140, 0, 0.9)",
    gradientTo: "rgba(178, 34, 34, 0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.autumn",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/autumn/thumbnails/8962f7f4-b55b-4047-bc33-5beebdf8c6c3.jpg"),
      android: cdn("wallpapers/autumn/thumbnails/31bbb507-16e2-458f-8e89-d70eaab4f52f.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/autumn/thumbnails/e4103740-5d1d-46a1-acda-db34e552c6a9.jpg"),
        category: "Forests",
        title: "Golden Autumn Forest",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/autumn/thumbnails/0ee9db3f-7af7-4cc2-8fc1-b8e3e7a8ebaa.jpg"),
        category: "Leaves",
        title: "Falling Autumn Leaves",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/autumn/thumbnails/6b8cc0c5-0ce4-4150-8d60-a0332587bb29.jpg"),
        category: "Lakes",
        title: "Autumn Lake Reflection",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/autumn/thumbnails/2bd04f8d-007f-4221-b7e4-f047e48c2ffd.jpg"),
        category: "Mountains",
        title: "Autumn Mountain Valley",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/car/thumbnails/72f1600d-b927-4db8-8a6e-4492e77a5bd7.jpg"),
        category: "Roads",
        title: "Autumn Scenic Road",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/autumn/thumbnails/4260ad1c-c09f-41b6-8409-fc24efead115.jpg"),
        category: "Sunset",
        title: "Warm Fall Sunset",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=autumn",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=autumn",
    }
  },

  "car": {
    id: "car",
    name: "Car 4K Wallpapers",
    tagline: "Supercar & Sports Car Wallpapers",
    description: "Explore stunning high-performance car wallpapers featuring supercars, sports cars, luxury vehicles and classic automobiles in ultra sharp 4K quality.",
    
    // Branding - Black to Red gradient
    logo: cdn("app-assets/car-app/car-logo.png"),
    primaryColor: "#1C1C1C",
    secondaryColor: "#FF0000",
    gradientFrom: "rgba(28,28,28,0.9)",
    gradientTo: "rgba(255,0,0,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.car",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/car/thumbnails/8b82bc0c-4c36-4082-b7ab-51cd5b1535a9.jpg"),
      android: cdn("wallpapers/car/thumbnails/4d7f88f2-bd00-4982-af63-5a113703075b.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/car/thumbnails/970a5c0c-f2ef-4037-a46c-597babd5105a.jpg"),
        category: "Supercars",
        title: "Red Supercar Beast",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/car/thumbnails/7aabf455-2c61-4731-83ab-92d4e50bc682.jpg"),
        category: "Sports Cars",
        title: "Speed Machine",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/car/thumbnails/d6c573f3-890e-447e-9cea-a346adb36348.jpg"),
        category: "Luxury",
        title: "Luxury Sedan Night",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/car/thumbnails/a7e36087-79cb-4e46-bb32-dbd13a04a31c.jpg"),
        category: "Classic",
        title: "Vintage Classic Car",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/car/thumbnails/d479ccf7-5d19-4332-85cb-37c65f80b70b.jpg"),
        category: "Racing",
        title: "Race Track Monster",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/car/thumbnails/0acdbd03-249a-439d-b289-75fc979462b6.jpg"),
        category: "Hypercar",
        title: "Ultimate Hypercar",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=car",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=car",
    }
  },

  "cat": {
    id: "cat",
    name: "Cat 4K Wallpapers",
    tagline: "Cute Cat HD & 4K Wallpapers",
    description: "Enjoy adorable cat wallpapers featuring cute kittens, playful cats and beautiful feline portraits in high resolution 4K quality.",
    
    // Branding - Pink to Orange gradient
    logo: cdn("app-assets/cat-app/cat-logo.png"),
    primaryColor: "#FFB6C1",
    secondaryColor: "#FFA500",
    gradientFrom: "rgba(255,182,193,0.9)",
    gradientTo: "rgba(255,165,0,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.cat",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/cat/thumbnails/86e791ca-7710-4497-ba60-c9a283a806d3.jpg"),
      android: cdn("wallpapers/cat/thumbnails/faf956a0-d325-4cd6-8446-ad6f9f0e1d2e.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/cat/thumbnails/d8c92be5-589b-4860-bf0f-fceb2ce33acf.jpg"),
        category: "Kittens",
        title: "Adorable Kitten Eyes",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/cat/thumbnails/8be62477-3402-4e48-96a3-4a6a20fe1191.jpg"),
        category: "Cute",
        title: "Sleeping Kitty",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/cat/thumbnails/01ac4585-a392-4061-8192-15fd3a35e63f.jpg"),
        category: "Playful",
        title: "Playful Cat Jump",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/cat/thumbnails/9e53f7ff-be35-4081-847c-0b0473a5f38d.jpg"),
        category: "Portrait",
        title: "Fluffy Cat Portrait",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/cat/thumbnails/239b6c33-8959-4cc6-9d22-4ee1eef8a2f0.png"),
        category: "Funny",
        title: "Funny Cat Expression",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/christmas/thumbnails/c633aa1f-2d71-44b7-a9a6-71d544adcdb0.png"),
        category: "Pets",
        title: "Lovely Home Cat",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=cat",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=cat",
    }
  },

  "christmas": {
    id: "christmas",
    name: "Christmas 4K Wallpapers",
    tagline: "Festive Christmas HD & 4K Wallpapers",
    description: "Celebrate the magic of Christmas with beautiful festive wallpapers. Discover Christmas trees, snow, Santa Claus, decorations, cozy winter scenes and holiday lights in stunning 4K quality.",
    
    // Branding - Red to Green gradient
    logo: cdn("app-assets/christmas-app/christmas-logo.png"),
    primaryColor: "#B22222",
    secondaryColor: "#228B22",
    gradientFrom: "rgba(178,34,34,0.9)",
    gradientTo: "rgba(34,139,34,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.christmas",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/christmas/thumbnails/25bc988b-5046-41c4-91e2-05e8818d4b47.png"),
      android: cdn("wallpapers/christmas/thumbnails/c7207c56-a5ce-43ad-9be7-2ddd660403e6.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/christmas/thumbnails/07066c34-417d-4587-b032-d5073fa3b0bd.png"),
        category: "Christmas Tree",
        title: "Decorated Christmas Tree",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/christmas/thumbnails/f0de4cbe-addb-467e-bd33-16cadb29c6e1.png"),
        category: "Snow",
        title: "Snowy Christmas Village",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/christmas/thumbnails/b53fb45b-4a6f-4f52-a473-af7ed5759b61.png"),
        category: "Santa",
        title: "Santa Claus Night Ride",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/christmas/thumbnails/cca2bc4e-4a31-4764-8bed-3d002cb9dc9f.png"),
        category: "Lights",
        title: "Christmas Lights Glow",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/christmas/thumbnails/6090ae35-3b48-4e48-985c-dca6e70460fe.png"),
        category: "Winter",
        title: "Cozy Winter Cabin",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/christmas/thumbnails/8082476d-f216-4dc8-83b8-6b2a2023afbd.png"),
        category: "Holiday",
        title: "Festive Holiday Mood",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=christmas",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=christmas",
    }
  },

  "couple": {
    id: "couple",
    name: "Couple 4K Wallpapers",
    tagline: "Romantic Couple HD & 4K Wallpapers",
    description: "Discover romantic couple wallpapers featuring love moments, aesthetic relationships, sunset silhouettes, romantic scenes and beautiful love-themed backgrounds in stunning 4K resolution.",
    
    // Branding - Pink to Red gradient
    logo: cdn("app-assets/couple-app/couple-logo.png"),
    primaryColor: "#FF69B4",
    secondaryColor: "#FF1493",
    gradientFrom: "rgba(255,105,180,0.9)",
    gradientTo: "rgba(255,20,147,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.couple",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/couple/thumbnails/5fe1aa14-e7d6-411b-9321-7c301c1bb5da.jpg"),
      android: cdn("wallpapers/couple/thumbnails/20292d3a-529c-445c-877c-081cad5e0ce8.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/couple/thumbnails/f2a010a6-5a3b-4eaa-9ffd-c2533d8fd0fe.jpg"),
        category: "Love",
        title: "Romantic Sunset Couple",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/couple/thumbnails/f6a02f87-d11b-4a49-b770-7d291b6e4893.jpg"),
        category: "Silhouette",
        title: "Couple Sunset Silhouette",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/couple/thumbnails/d73a71dc-cc96-4148-b223-bba95bd24a06.jpg"),
        category: "Aesthetic",
        title: "Cute Couple Aesthetic",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/couple/thumbnails/a8e54176-1e23-4ac8-81f3-f1cd632958e6.jpg"),
        category: "Romance",
        title: "Romantic Evening Walk",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/couple/thumbnails/484257c3-4e41-46af-8b34-3e909773334c.jpg"),
        category: "Hug",
        title: "Warm Couple Hug",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/couple/thumbnails/5495107b-dcf6-49e8-8c49-ab6a97424836.jpg"),
        category: "Date",
        title: "Cute Date Moment",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=couple",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=couple",
    }
  },

  "cutegirl": {
    id: "cutegirl",
    name: "Cute Girl 4K Wallpapers",
    tagline: "Stylish & Cute Girl HD Wallpapers",
    description: "Discover beautiful cute girl wallpapers featuring stylish portraits, aesthetic looks, fashion vibes and stunning girl photography in high-quality 4K resolution.",
    
    // Branding - Pink to Purple gradient
    logo: cdn("app-assets/cutegirl-app/cutegirl-logo.png"),
    primaryColor: "#FF69B4",
    secondaryColor: "#9370DB",
    gradientFrom: "rgba(255,105,180,0.9)",
    gradientTo: "rgba(147,112,219,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.cutegirl",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/cute-girl/thumbnails/9a9874dd-47a1-47e8-baff-7efd1bb33345.png"),
      android: cdn("wallpapers/christmas/thumbnails/c7207c56-a5ce-43ad-9be7-2ddd660403e6.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/cute-girl/thumbnails/4f3ef812-634e-4f14-a8a3-873c89e74eef.png"),
        category: "Portrait",
        title: "Cute Girl Portrait",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/cute-girl/thumbnails/1fabb649-84cb-46c5-b71d-675b1ad3534d.png"),
        category: "Fashion",
        title: "Stylish Girl Look",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/cute-girl/thumbnails/a54c0591-80d8-481e-9d56-42d4a348c5fc.png"),
        category: "Aesthetic",
        title: "Aesthetic Girl Vibes",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/cute-girl/thumbnails/19662392-55d3-46cf-bce5-eee63ca81cf4.png"),
        category: "Smile",
        title: "Beautiful Smile",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/cute-girl/thumbnails/d1d00c7a-abdd-4a76-a12d-ab7b819e6416.png"),
        category: "Lifestyle",
        title: "Modern Lifestyle Portrait",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/christmas/thumbnails/c7207c56-a5ce-43ad-9be7-2ddd660403e6.png"),
        category: "Cute",
        title: "Adorable Girl Style",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=cutegirl",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=cutegirl",
    }
  },

  "chocolate": {
    id: "chocolate",
    name: "Chocolate 4K Wallpapers",
    tagline: "Delicious Chocolate HD & 4K Wallpapers",
    description: "Indulge in delicious chocolate wallpapers featuring rich cocoa, sweet desserts, chocolate bars, truffles and mouth-watering chocolate photography in stunning 4K resolution.",
    
    // Branding - Brown to Dark Brown gradient
    logo: cdn("app-assets/chocolate-app/chocolate-logo.png"),
    primaryColor: "#8B4513",
    secondaryColor: "#3D2314",
    gradientFrom: "rgba(139,69,19,0.9)",
    gradientTo: "rgba(61,35,20,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.chocolate",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/chocolate/thumbnails/b7fb3d47-b05a-459c-a045-bf0a4ee88a4e.jpg"),
      android: cdn("wallpapers/chocolate/thumbnails/55f3d448-409b-48f3-a4f5-c69c177793f2.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/chocolate/thumbnails/fa108139-7416-4f24-8b72-f0803facab37.jpg"),
        category: "Dark Chocolate",
        title: "Rich Dark Chocolate",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/chocolate/thumbnails/aa5e807d-e456-42c6-a402-c26291b4cc26.jpg"),
        category: "Dessert",
        title: "Chocolate Dessert Delight",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/chocolate/thumbnails/b7fb3d47-b05a-459c-a045-bf0a4ee88a4e.jpg"),
        category: "Truffle",
        title: "Gourmet Chocolate Truffles",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/chocolate/thumbnails/a9fa935f-f709-48ee-86df-10b3daaea500.jpg"),
        category: "Cocoa",
        title: "Hot Cocoa Comfort",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/chocolate/thumbnails/b44fab50-8f8a-47f8-9fb6-fc947348ae54.jpg"),
        category: "Bars",
        title: "Premium Chocolate Bars",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/chocolate/thumbnails/55f3d448-409b-48f3-a4f5-c69c177793f2.jpg"),
        category: "Sweet",
        title: "Sweet Chocolate Dreams",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=chocolate",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=chocolate",
    }
  },

  "cosmic": {
    id: "cosmic",
    name: "Cosmic 4K Wallpapers",
    tagline: "Space & Galaxy HD & 4K Wallpapers",
    description: "Explore the universe with stunning cosmic wallpapers featuring galaxies, nebulas, stars, planets, black holes and breathtaking space photography in ultra HD 4K resolution.",
    
    // Branding - Deep Blue to Purple gradient
    logo: cdn("app-assets/cosmic-app/cosmic-logo.png"),
    primaryColor: "#191970",
    secondaryColor: "#8A2BE2",
    gradientFrom: "rgba(25,25,112,0.9)",
    gradientTo: "rgba(138,43,226,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.cosmic",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/cosmic/thumbnails/7f1c9fdd-8c57-411f-aefd-a9fc01d372ee.jpg"),
      android: cdn("wallpapers/cosmic/thumbnails/2f5660c7-ccc1-488b-a541-5cd887541448.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/cosmic/thumbnails/94fb0155-cb69-4abc-abe3-b5b073547d1b.jpg"),
        category: "Galaxy",
        title: "Spiral Galaxy Wonder",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/cosmic/thumbnails/a80cdaa8-fab7-4ba6-92bd-8999ab9ccd0b.jpg"),
        category: "Nebula",
        title: "Colorful Nebula Cloud",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/cosmic/thumbnails/14eec0f4-9ef1-4335-b9f1-f41b4393f5a0.jpg"),
        category: "Stars",
        title: "Starfield Universe",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/cosmic/thumbnails/7f1c9fdd-8c57-411f-aefd-a9fc01d372ee.jpg"),
        category: "Planets",
        title: "Alien Planet Landscape",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/cosmic/thumbnails/31bc7a17-c645-456a-a046-b5772f0b1448.jpg"),
        category: "Black Hole",
        title: "Mysterious Black Hole",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/cosmic/thumbnails/2f5660c7-ccc1-488b-a541-5cd887541448.jpg"),
        category: "Space",
        title: "Deep Space Exploration",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=cosmic",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=cosmic",
    }
  },

  "digitalart": {
    id: "digitalart",
    name: "Digital Art 4K Wallpapers",
    tagline: "Creative Digital Art HD & 4K Wallpapers",
    description: "Discover stunning digital art wallpapers featuring creative illustrations, concept art, fantasy designs, abstract visuals and artistic masterpieces in high-quality 4K resolution.",
    
    // Branding - Cyan to Magenta gradient
    logo: cdn("app-assets/digitalart-app/digitalart-logo.png"),
    primaryColor: "#00CED1",
    secondaryColor: "#FF00FF",
    gradientFrom: "rgba(0,206,209,0.9)",
    gradientTo: "rgba(255,0,255,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.digitalart",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/anime/thumbnails/da8059e4-efc5-4982-b84e-7b2bdf98c77d.jpg"),
      android: cdn("wallpapers/fantasy/thumbnails/fb55b19e-71a9-4a1f-858a-8f787fb6cbe0.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/anime/thumbnails/da8059e4-efc5-4982-b84e-7b2bdf98c77d.jpg"),
        category: "Illustration",
        title: "Creative Digital Illustration",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/fantasy/thumbnails/fb55b19e-71a9-4a1f-858a-8f787fb6cbe0.jpg"),
        category: "Concept Art",
        title: "Futuristic Concept Art",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/abstract/thumbnails/248e39c6-6ed3-4537-8859-2ed9dd72273a.jpg"),
        category: "Abstract",
        title: "Abstract Digital Creation",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/urban/thumbnails/5d823ad6-3e05-42f8-a3dc-271d56b234f2.jpg"),
        category: "Fantasy",
        title: "Fantasy Digital World",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/mountains/thumbnails/828fcdec-4d5f-409d-aed0-820f1bc6f22a.jpg"),
        category: "Surreal",
        title: "Surreal Art Vision",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/digital-art/thumbnails/c835a268-9125-4461-887e-214a66879495.png"),
        category: "Modern",
        title: "Modern Digital Masterpiece",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=digitalart",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=digitalart",
    }
  },

  "dog": {
    id: "dog",
    name: "Dog 4K Wallpapers",
    tagline: "Adorable Dog HD & 4K Wallpapers",
    description: "Enjoy adorable dog wallpapers featuring cute puppies, loyal companions, playful dogs and beautiful canine portraits in high resolution 4K quality.",
    
    // Branding - Golden Brown to Tan gradient
    logo: cdn("app-assets/dog-app/dog-logo.png"),
    primaryColor: "#DAA520",
    secondaryColor: "#D2691E",
    gradientFrom: "rgba(218,165,32,0.9)",
    gradientTo: "rgba(210,105,30,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.dog",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/dog/thumbnails/ccb30450-55b6-4577-8b5c-f2129bafd192.png"),
      android: cdn("wallpapers/dog/thumbnails/b0925a08-f178-4cd4-9da1-8ddfdd57aec7.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/dog/thumbnails/ccb30450-55b6-4577-8b5c-f2129bafd192.png"),
        category: "Puppies",
        title: "Adorable Puppy Eyes",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/dog/thumbnails/eeca7ad7-ff47-4af6-a744-5ca8c694de42.png"),
        category: "Golden",
        title: "Golden Retriever Smile",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/dog/thumbnails/1b4c47fb-e825-4b31-9944-a70c7354a184.png"),
        category: "Playful",
        title: "Playful Dog Joy",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/dog/thumbnails/869aa00a-85da-413c-b4df-392f5d9699f1.png"),
        category: "Husky",
        title: "Beautiful Husky Portrait",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/couple/thumbnails/323089d0-ef49-4a68-8a14-f8c66bc66271.jpg"),
        category: "Loyal",
        title: "Loyal Companion Friend",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/dog/thumbnails/b0925a08-f178-4cd4-9da1-8ddfdd57aec7.png"),
        category: "Cute",
        title: "Cute Dog Moments",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=dog",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=dog",
    }
  },

  "dolphin": {
    id: "dolphin",
    name: "Dolphin 4K Wallpapers",
    tagline: "Beautiful Dolphin HD & 4K Wallpapers",
    description: "Dive into the ocean with stunning dolphin wallpapers featuring playful dolphins, underwater scenes, ocean waves and beautiful marine life photography in breathtaking 4K resolution.",
    
    // Branding - Ocean Blue to Turquoise gradient
    logo: cdn("app-assets/dolphin-app/dolphin-logo.png"),
    primaryColor: "#0077BE",
    secondaryColor: "#40E0D0",
    gradientFrom: "rgba(0,119,190,0.9)",
    gradientTo: "rgba(64,224,208,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.dolphin",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/dolphin/thumbnails/8d7af526-2a2a-479c-b355-04dd39218592.jpg"),
      android: cdn("wallpapers/dolphin/thumbnails/c22065ea-891f-4dcb-a59e-444eb9c1fea6.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/dolphin/thumbnails/8d7af526-2a2a-479c-b355-04dd39218592.jpg"),
        category: "Jumping",
        title: "Dolphin Jumping Joy",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/dolphin/thumbnails/a892ea3c-124e-4428-bf37-4e2aac68e33c.jpg"),
        category: "Underwater",
        title: "Underwater Dolphin Grace",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/dolphin/thumbnails/86bab081-0b26-4d73-8fbc-a00ad8fa6661.jpg"),
        category: "Ocean",
        title: "Ocean Dolphin Swim",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/dolphin/thumbnails/c22065ea-891f-4dcb-a59e-444eb9c1fea6.jpg"),
        category: "Sunset",
        title: "Dolphin Sunset Silhouette",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/dolphin/thumbnails/941acc16-133e-458c-9325-c3d14e4f1852.jpg"),
        category: "Playful",
        title: "Playful Dolphin Pod",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/dolphin/thumbnails/2dfca820-6721-4fb0-bc45-73adc106cc21.jpg"),
        category: "Marine",
        title: "Beautiful Marine Life",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=dolphin",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=dolphin",
    }
  },

  "fantasy": {
    id: "fantasy",
    name: "Fantasy 4K Wallpapers",
    tagline: "Magical Fantasy HD & 4K Wallpapers",
    description: "Enter magical worlds with stunning fantasy wallpapers featuring mythical creatures, enchanted landscapes, dragons, castles and epic fantasy art in breathtaking 4K resolution.",
    
    // Branding - Purple to Gold gradient
    logo: cdn("app-assets/fantasy-app/fantasy-logo.png"),
    primaryColor: "#9932CC",
    secondaryColor: "#FFD700",
    gradientFrom: "rgba(153,50,204,0.9)",
    gradientTo: "rgba(255,215,0,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.fantasy",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/fantasy/thumbnails/fb55b19e-71a9-4a1f-858a-8f787fb6cbe0.jpg"),
      android: cdn("wallpapers/dragon/thumbnails/fc280887-0f7b-44c2-8d8b-8724f6a2233b.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/dragon/thumbnails/fc280887-0f7b-44c2-8d8b-8724f6a2233b.jpg"),
        category: "Dragons",
        title: "Majestic Dragon Flight",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/castle/thumbnails/83ab524b-e628-4ef3-a750-a2f48e0b6e17.jpg"),
        category: "Castles",
        title: "Enchanted Castle Kingdom",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/fantasy/thumbnails/f3d41e80-258b-4004-9667-79799efd9879.jpg"),
        category: "Magic",
        title: "Magical Forest Realm",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/fantasy/thumbnails/f785f7b5-e073-47db-a829-9876aa14dc61.jpg"),
        category: "Mythical",
        title: "Mythical Creatures World",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/dragon/thumbnails/9371a405-b83e-4b74-bc16-14d2fcf25831.jpg"),
        category: "Epic",
        title: "Epic Fantasy Battle",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/castle/thumbnails/1b8cf558-9d85-45b9-b45a-b1951b6df3e2.jpg"),
        category: "Enchanted",
        title: "Enchanted Landscape",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=fantasy",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=fantasy",
    }
  },

  "flower": {
    id: "flower",
    name: "Flower 4K Wallpapers",
    tagline: "Beautiful Flower HD & 4K Wallpapers",
    description: "Discover beautiful flower wallpapers featuring roses, tulips, sunflowers, cherry blossoms and stunning floral photography in vibrant 4K resolution.",
    
    // Branding - Pink to Rose gradient
    logo: cdn("app-assets/flower-app/flower-logo.png"),
    primaryColor: "#FF69B4",
    secondaryColor: "#FF1493",
    gradientFrom: "rgba(255,105,180,0.9)",
    gradientTo: "rgba(255,20,147,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.flower",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/nature/thumbnails/5473b45f-4d7a-42cd-b430-1e5da5769474.png"),
      android: cdn("wallpapers/flower/thumbnails/3cb55f9c-1bd7-4e20-9520-ce2ae5bec3e7.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/rose/thumbnails/1df49eff-8829-4134-83e0-5b883c305761.jpg"),
        category: "Roses",
        title: "Beautiful Red Roses",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/autumn/thumbnails/e4103740-5d1d-46a1-acda-db34e552c6a9.jpg"),
        category: "Tulips",
        title: "Colorful Tulip Garden",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/nature/thumbnails/5473b45f-4d7a-42cd-b430-1e5da5769474.png"),
        category: "Cherry Blossom",
        title: "Cherry Blossom Beauty",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/flower/thumbnails/3cb55f9c-1bd7-4e20-9520-ce2ae5bec3e7.png"),
        category: "Sunflower",
        title: "Bright Sunflower Field",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/holi/thumbnails/2a415625-3c0e-45a4-b3b0-c0308b9150c1.jpg"),
        category: "Orchid",
        title: "Elegant Orchid Bloom",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/winters/thumbnails/277173cb-3c6e-4f70-8647-06a15b318313.png"),
        category: "Mixed",
        title: "Beautiful Flower Bouquet",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=flower",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=flower",
    }
  },

  "foody": {
    id: "foody",
    name: "Foody 4K Wallpapers",
    tagline: "Delicious Food HD & 4K Wallpapers",
    description: "Indulge in mouth-watering food wallpapers featuring gourmet dishes, desserts, fruits, cuisine photography and delicious food art in stunning 4K resolution.",
    
    // Branding - Orange to Red gradient
    logo: cdn("app-assets/foody-app/foody-logo.png"),
    primaryColor: "#FF6347",
    secondaryColor: "#FF4500",
    gradientFrom: "rgba(255,99,71,0.9)",
    gradientTo: "rgba(255,69,0,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.foody",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/foody/thumbnails/4e851588-6954-4db2-844d-2d25079c1e31.png"),
      android: cdn("wallpapers/chocolate/thumbnails/5f792e61-eb2f-4ee9-95f2-06d4cb0de65a.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/chocolate/thumbnails/39d8fdb7-587c-45a4-96c4-edb237de764e.jpg"),
        category: "Strawberry",
        title: "Strawberry Dessert Delight",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/chocolate/thumbnails/fa108139-7416-4f24-8b72-f0803facab37.jpg"),
        category: "Chocolate",
        title: "Chocolate Bar Delight",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/chocolate/thumbnails/ee11cbd6-1409-4d88-aabf-962f29fae40f.jpg"),
        category: "Fruits",
        title: "Fresh Fruit Platter",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/foody/thumbnails/38a136ea-8095-4250-8666-9cc37fc6157d.png"),
        category: "Pizza",
        title: "Delicious Pizza Art",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/foody/thumbnails/63441cf1-4428-42e5-ae71-5ab7c6e8b152.png"),
        category: "Drinks",
        title: "Refreshing Beverages",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/foody/thumbnails/1adf1535-ba5c-44b0-810b-2b66c21bae3d.png"),
        category: "Cuisine",
        title: "World Cuisine Beauty",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=foody",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=foody",
    }
  },

  "jesus": {
    id: "jesus",
    name: "Jesus 4K Wallpapers",
    tagline: "Divine Jesus Christ HD & 4K Wallpapers",
    description: "Experience divine spirituality with stunning Jesus Christ wallpapers featuring religious art, cross imagery, biblical scenes and beautiful Christian devotional images in 4K resolution.",
    
    // Branding - Gold to White gradient
    logo: cdn("app-assets/jesus-app/jesus-logo.png"),
    primaryColor: "#FFD700",
    secondaryColor: "#F5F5DC",
    gradientFrom: "rgba(255,215,0,0.9)",
    gradientTo: "rgba(245,245,220,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.jesus",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/jesus/thumbnails/a1be2dcc-6ca0-4bc3-8904-edbdc347d5be.jpg"),
      android: cdn("wallpapers/jesus/thumbnails/fd76e6f0-fa3c-43b3-90e0-684a869f844a.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/jesus/thumbnails/69211e5f-d8ff-402d-ba1e-5bc7162714b6.jpg"),
        category: "Portrait",
        title: "Jesus Christ Portrait",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/jesus/thumbnails/16fdcab0-7196-4c3f-800e-ccb2f425a65f.jpg"),
        category: "Cross",
        title: "Holy Cross Symbol",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/jesus/thumbnails/fd76e6f0-fa3c-43b3-90e0-684a869f844a.jpg"),
        category: "Biblical",
        title: "Biblical Scene Art",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/jesus/thumbnails/a1be2dcc-6ca0-4bc3-8904-edbdc347d5be.jpg"),
        category: "Divine",
        title: "Divine Light of Christ",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/jesus/thumbnails/5b4caa61-7964-4955-a743-d8a5ee3d1eb1.jpg"),
        category: "Prayer",
        title: "Peaceful Prayer Moment",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/jesus/thumbnails/cdab9981-d77e-42fd-83f0-3c3a9ba0a01d.jpg"),
        category: "Sacred",
        title: "Sacred Heart of Jesus",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=jesus",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=jesus",
    }
  },

  "krishna": {
    id: "krishna",
    name: "Krishna 4K Wallpapers",
    tagline: "Divine Lord Krishna HD & 4K Wallpapers",
    description: "Transform your device with beautiful Lord Krishna wallpapers featuring divine art, Radha Krishna, flute playing Krishna, peacock feather imagery and devotional scenes in stunning 4K quality.",
    
    // Branding - Blue to Yellow gradient
    logo: cdn("app-assets/krishna-app/krishna-logo.png"),
    primaryColor: "#4169E1",
    secondaryColor: "#FFD700",
    gradientFrom: "rgba(65,105,225,0.9)",
    gradientTo: "rgba(255,215,0,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.krishna",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/krishna/thumbnails/82d686ae-b15b-41a5-8f22-7b09e8b418f5.jpg"),
      android: cdn("wallpapers/krishna/thumbnails/2edddf47-662d-4671-9440-feefd9216ceb.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/krishna/thumbnails/82d686ae-b15b-41a5-8f22-7b09e8b418f5.jpg"),
        category: "Divine",
        title: "Divine Krishna Portrait",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/krishna/thumbnails/a738840d-c697-40b0-b4aa-3761f3de7108.jpg"),
        category: "Radha Krishna",
        title: "Radha Krishna Love",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/krishna/thumbnails/4b745be0-1a9e-4c65-ac85-c5ce53fd5dc8.jpg"),
        category: "Flute",
        title: "Krishna Playing Flute",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/krishna/thumbnails/2edddf47-662d-4671-9440-feefd9216ceb.jpg"),
        category: "Peacock",
        title: "Peacock Feather Beauty",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/krishna/thumbnails/05491c8e-5f51-494b-a6d2-423a5d4af904.jpg"),
        category: "Baby Krishna",
        title: "Adorable Baby Krishna",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/krishna/thumbnails/968adfb5-5461-4c8d-b51b-95ef223df730.jpg"),
        category: "Spiritual",
        title: "Spiritual Krishna Art",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=krishna",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=krishna",
    }
  },

  "bird": {
    id: "bird",
    name: "Bird 4K Wallpapers",
    tagline: "Beautiful Bird HD & 4K Wallpapers",
    description: "Discover stunning bird wallpapers featuring colorful parrots, majestic eagles, peacocks, hummingbirds and beautiful avian photography in breathtaking 4K resolution.",
    
    // Branding - Sky Blue to Green gradient
    logo: cdn("app-assets/bird-app/bird-logo.png"),
    primaryColor: "#87CEEB",
    secondaryColor: "#32CD32",
    gradientFrom: "rgba(135,206,235,0.9)",
    gradientTo: "rgba(50,205,50,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.bird",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/bird/thumbnails/0376fcf4-2151-4410-b9a0-924dfe3d799a.jpg"),
      android: cdn("wallpapers/bird/thumbnails/3a260780-3b2a-4580-a48b-9e9ce08f5af5.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/bird/thumbnails/3a260780-3b2a-4580-a48b-9e9ce08f5af5.jpg"),
        category: "Parrot",
        title: "Colorful Parrot Beauty",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/pink/thumbnails/ab3b8027-599e-427a-b68e-e3254291c1ed.jpg"),
        category: "Eagle",
        title: "Majestic Eagle Flight",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/krishna/thumbnails/8ac4d2e8-7bdd-4d11-9f7a-e2eb9e989dac.jpg"),
        category: "Peacock",
        title: "Peacock Feathers Display",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/bird/thumbnails/c97546c2-b9cb-4855-9eff-644e8f0cf7f5.jpg"),
        category: "Hummingbird",
        title: "Tiny Hummingbird Wonder",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/bird/thumbnails/9e7b3028-37c0-4b5e-a033-ef8ffc47b984.jpg"),
        category: "Owl",
        title: "Wise Owl Portrait",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/bird/thumbnails/782ae13e-051b-46d1-8115-17cb82558b26.jpg"),
        category: "Tropical",
        title: "Tropical Bird Paradise",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=bird",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=bird",
    }
  },

  "cutedoll": {
    id: "cutedoll",
    name: "Cute Doll 4K Wallpapers",
    tagline: "Adorable Cute Doll HD & 4K Wallpapers",
    description: "Discover adorable cute doll wallpapers featuring beautiful dolls, kawaii art, barbie dolls, anime dolls and charming doll photography in stunning 4K resolution.",
    
    // Branding - Pink to Lavender gradient
    logo: cdn("app-assets/cutedoll-app/cutedoll-logo.png"),
    primaryColor: "#FFB6C1",
    secondaryColor: "#E6E6FA",
    gradientFrom: "rgba(255,182,193,0.9)",
    gradientTo: "rgba(230,230,250,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.cutedoll",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/cute-doll/thumbnails/95e83105-e495-4e9b-8d97-1a8e320ebacd.jpg"),
      android: cdn("wallpapers/cute-doll/thumbnails/dbe2c013-a8ef-4448-995d-d252f49e853c.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/cute-doll/thumbnails/1b06367c-fba4-4255-86e4-d9b1eff32925.jpg"),
        category: "Barbie",
        title: "Beautiful Barbie Doll",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/cute-girl/thumbnails/49ee61b1-9561-427c-83c6-9b3f574844a3.png"),
        category: "Kawaii",
        title: "Kawaii Doll Cuteness",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/cute-doll/thumbnails/2efe080e-d87e-4819-927c-088bbe06cc44.jpg"),
        category: "Anime",
        title: "Anime Style Doll",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/cute-doll/thumbnails/71bf2d3e-ba8d-4075-a6d0-18f1cbda7ac2.jpg"),
        category: "Vintage",
        title: "Vintage Doll Beauty",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/cute-doll/thumbnails/b6169158-55e1-433d-9df7-ddae7d8afc8d.jpg"),
        category: "Princess",
        title: "Princess Doll Charm",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/cute-doll/thumbnails/f2213ffd-ec20-46c5-869e-2b7c834ee402.jpg"),
        category: "Adorable",
        title: "Adorable Doll Collection",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=cutedoll",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=cutedoll",
    }
  },

  "happybirthday": {
    id: "happybirthday",
    name: "Happy Birthday 4K Wallpapers",
    tagline: "Festive Birthday HD & 4K Wallpapers",
    description: "Celebrate special moments with beautiful birthday wallpapers featuring cakes, balloons, confetti, party decorations and joyful birthday themes in stunning 4K resolution.",
    
    // Branding - Rainbow Party gradient
    logo: cdn("app-assets/happybirthday-app/happybirthday-logo.png"),
    primaryColor: "#FF69B4",
    secondaryColor: "#FFD700",
    gradientFrom: "rgba(255,105,180,0.9)",
    gradientTo: "rgba(255,215,0,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.happybirthday",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/happy-birthday/thumbnails/6439ddc5-005b-40b1-aefb-b9658f874016.jpg"),
      android: cdn("wallpapers/happy-birthday/thumbnails/fec4514f-b186-4b8d-9f3f-2baacd362193.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/happy-birthday/thumbnails/14409e44-37e0-4bd9-a9ac-dcb678a724d2.jpg"),
        category: "Cake",
        title: "Birthday Cake Celebration",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/happy-birthday/thumbnails/057b6147-0acb-4a8f-b28d-f4d6d46baf6d.jpg"),
        category: "Balloons",
        title: "Colorful Birthday Balloons",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/happy-birthday/thumbnails/b079ad18-ca07-4bb6-8278-85fa2be35b6a.jpg"),
        category: "Confetti",
        title: "Party Confetti Joy",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/happy-birthday/thumbnails/3387da1b-1706-4307-bf6b-ff9df75c8aaa.jpg"),
        category: "Candles",
        title: "Birthday Candles Glow",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/happy-birthday/thumbnails/60ecb2b0-516b-4b49-8139-ef5711f455a1.jpg"),
        category: "Party",
        title: "Birthday Party Vibes",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/happy-birthday/thumbnails/0aed9d95-7572-4c22-a887-dd9f2298f2bc.jpg"),
        category: "Wishes",
        title: "Birthday Wishes Art",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=happybirthday",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=happybirthday",
    }
  },

  "happynewyear": {
    id: "happynewyear",
    name: "Happy New Year 4K Wallpapers",
    tagline: "Festive New Year HD & 4K Wallpapers",
    description: "Welcome the new year with stunning celebration wallpapers featuring fireworks, countdown, party themes, sparkles and joyful new year imagery in brilliant 4K resolution.",
    
    // Branding - Gold to Black gradient
    logo: cdn("app-assets/happynewyear-app/happynewyear-logo.png"),
    primaryColor: "#FFD700",
    secondaryColor: "#1C1C1C",
    gradientFrom: "rgba(255,215,0,0.9)",
    gradientTo: "rgba(28,28,28,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.happynewyear",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/happy-new-year/thumbnails/4f8aaa26-45ca-4e60-9294-e8c4a0a344bb.png"),
      android: cdn("wallpapers/happy-new-year/thumbnails/164aaad5-a2a9-427a-9b71-51600840db06.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/happy-new-year/thumbnails/12641b1b-dbc7-4ca5-be1c-9db3514c35dc.png"),
        category: "Fireworks",
        title: "New Year Fireworks",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/happy-new-year/thumbnails/88bff834-f680-48dd-b7e2-0cf18f6713b4.png"),
        category: "Countdown",
        title: "Midnight Countdown",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/happy-new-year/thumbnails/7bc17821-06c9-4e9a-9bc6-024870f95257.png"),
        category: "Sparkle",
        title: "Sparkling New Year",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/happy-new-year/thumbnails/2305abac-b107-475c-8e22-c9d7c8f9755f.png"),
        category: "Party",
        title: "New Year Party Night",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/happy-new-year/thumbnails/3170bbcf-9036-45db-ad05-4265b979b426.png"),
        category: "Celebration",
        title: "New Year Celebration",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/happy-new-year/thumbnails/b2705b21-31e7-42c1-9a3f-a79756f8540e.png"),
        category: "Golden",
        title: "Golden New Year Wishes",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=happynewyear",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=happynewyear",
    }
  },

  "lion": {
    id: "lion",
    name: "Lion 4K Wallpapers",
    tagline: "Majestic Lion HD & 4K Wallpapers",
    description: "Experience the king of the jungle with stunning lion wallpapers featuring majestic manes, powerful poses, wildlife photography and fierce lion portraits in breathtaking 4K resolution.",
    
    // Branding - Golden Brown to Dark Orange gradient
    logo: cdn("app-assets/lion-app/lion-logo.png"),
    primaryColor: "#DAA520",
    secondaryColor: "#8B4513",
    gradientFrom: "rgba(218,165,32,0.9)",
    gradientTo: "rgba(139,69,19,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.lion",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/lion/thumbnails/7aa93e42-3c10-40c2-a61a-5a3048bb7ab2.jpg"),
      android: cdn("wallpapers/lion/thumbnails/5a24735f-f225-4ffd-916d-0c3b171256a9.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/lion/thumbnails/e836acdc-88fe-40c1-9a30-5e4a82c95875.jpg"),
        category: "Majestic",
        title: "King of the Jungle",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/lion/thumbnails/27fb6071-bfd7-417c-b90b-3dc9b69128d2.jpg"),
        category: "Portrait",
        title: "Lion Portrait Majesty",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/lion/thumbnails/5096d591-14c5-467c-9197-1939cb097376.jpg"),
        category: "Roar",
        title: "Powerful Lion Roar",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/lion/thumbnails/96d18cc0-7c8c-4448-91d1-83545a5f011e.jpg"),
        category: "Family",
        title: "Lion Pride Family",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/lion/thumbnails/7c828a44-4e24-4116-816a-64ebe3a00126.jpg"),
        category: "Safari",
        title: "Safari Lion Adventure",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/lion/thumbnails/08d3513a-2e07-4d67-a5f8-ad952c258570.jpg"),
        category: "Fierce",
        title: "Fierce Lion Gaze",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=lion",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=lion",
    }
  },

  "moon": {
    id: "moon",
    name: "Moon 4K Wallpapers",
    tagline: "Beautiful Moon HD & 4K Wallpapers",
    description: "Discover mesmerizing moon wallpapers featuring full moons, lunar phases, moonlit nights, crescent moons and stunning lunar photography in brilliant 4K resolution.",
    
    // Branding - Silver to Dark Blue gradient
    logo: cdn("app-assets/moon-app/moon-logo.png"),
    primaryColor: "#C0C0C0",
    secondaryColor: "#191970",
    gradientFrom: "rgba(192,192,192,0.9)",
    gradientTo: "rgba(25,25,112,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.moon",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/anime/thumbnails/7933811b-6360-42a9-bd8d-e6c97e63a53b.jpg"),
      android: cdn("wallpapers/moon/thumbnails/f89db78a-62e5-484f-8542-5cf1618fc221.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/moon/thumbnails/f89db78a-62e5-484f-8542-5cf1618fc221.jpg"),
        category: "Full Moon",
        title: "Bright Full Moon Night",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/mountains/thumbnails/828fcdec-4d5f-409d-aed0-820f1bc6f22a.jpg"),
        category: "Crescent",
        title: "Beautiful Crescent Moon",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/moon/thumbnails/7297d988-2413-4229-ac8b-92e3b2cbf3ac.jpg"),
        category: "Moonlit",
        title: "Moonlit Night Scene",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/mountains/thumbnails/02946174-431e-4246-b870-d4cb8621728d.jpg"),
        category: "Lunar",
        title: "Lunar Surface Beauty",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/moon/thumbnails/0af3ffcf-4626-4ddc-8196-c397c891bea8.jpg"),
        category: "Blood Moon",
        title: "Red Blood Moon",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/moon/thumbnails/302b343b-393a-4549-a988-a11023abd535.jpg"),
        category: "Supermoon",
        title: "Supermoon Wonder",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=moon",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=moon",
    }
  },

  "mountains": {
    id: "mountains",
    name: "Mountains 4K Wallpapers",
    tagline: "Majestic Mountain HD & 4K Wallpapers",
    description: "Explore breathtaking mountain wallpapers featuring snow-capped peaks, alpine landscapes, misty valleys, rocky terrains and stunning mountain photography in 4K resolution.",
    
    // Branding - Mountain Blue to White gradient
    logo: cdn("app-assets/mountains-app/mountains-logo.png"),
    primaryColor: "#4682B4",
    secondaryColor: "#F5F5F5",
    gradientFrom: "rgba(70,130,180,0.9)",
    gradientTo: "rgba(245,245,245,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.mountains",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/castle/thumbnails/54023a2f-7709-4477-94c4-bb582f78333e.jpg"),
      android: cdn("wallpapers/nature/thumbnails/641170a5-c4a4-478a-9dbe-42e65e95d377.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/winters/thumbnails/d44a6d11-0b95-4b55-a824-8deb4a6f6099.png"),
        category: "Snowy",
        title: "Snow-Capped Mountain Peak",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/instawall/thumbnails/1b880b31-0777-4be0-b8e3-e2564ddd4fb4.jpg"),
        category: "Alpine",
        title: "Alpine Mountain Beauty",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/mountains/thumbnails/802d6bde-396d-411e-a18f-16bf919a6275.png"),
        category: "Misty",
        title: "Misty Mountain Morning",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/mountains/thumbnails/828fcdec-4d5f-409d-aed0-820f1bc6f22a.jpg"),
        category: "Valley",
        title: "Mountain Valley View",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/digital-art/thumbnails/02fe1962-3bda-4667-903e-1a78ebf72ab8.jpg"),
        category: "Rocky",
        title: "Rocky Mountain Range",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/car/thumbnails/b6699710-8a6e-45a4-a387-49ff510ce9db.jpg"),
        category: "Sunset",
        title: "Mountain Sunset Glory",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=mountains",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=mountains",
    }
  },

  "night": {
    id: "night",
    name: "Night 4K Wallpapers",
    tagline: "Beautiful Night HD & 4K Wallpapers",
    description: "Experience the beauty of nighttime with stunning night wallpapers featuring starry skies, city lights, moonlit scenes, northern lights and mesmerizing night photography in 4K resolution.",
    
    // Branding - Dark Blue to Purple gradient
    logo: cdn("app-assets/night-app/night-logo.png"),
    primaryColor: "#191970",
    secondaryColor: "#4B0082",
    gradientFrom: "rgba(25,25,112,0.9)",
    gradientTo: "rgba(75,0,130,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.night",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/anime/thumbnails/7933811b-6360-42a9-bd8d-e6c97e63a53b.jpg"),
      android: cdn("wallpapers/digital-art/thumbnails/02fe1962-3bda-4667-903e-1a78ebf72ab8.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/nature/thumbnails/fb2e71a8-64ee-4eee-9a5c-7c987b8255c3.png"),
        category: "Starry",
        title: "Starry Night Sky",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/beach/thumbnails/81bf937b-1f10-462c-9733-21a80c5333ab.png"),
        category: "City",
        title: "City Lights at Night",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/cosmic/thumbnails/302ecfbd-e1b3-4d21-8735-744f50f174a0.jpg"),
        category: "Aurora",
        title: "Northern Lights Wonder",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/fantasy/thumbnails/96299fa7-7d30-4272-a9cf-952976075f10.jpg"),
        category: "Moonlit",
        title: "Moonlit Night Beauty",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/moon/thumbnails/7297d988-2413-4229-ac8b-92e3b2cbf3ac.jpg"),
        category: "Dark",
        title: "Dark Night Aesthetic",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/night/thumbnails/cdd3465f-e1e1-4193-b54a-10d703018156.jpg"),
        category: "Milky Way",
        title: "Milky Way Galaxy Night",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=night",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=night",
    }
  },

  "panda": {
    id: "panda",
    name: "Panda 4K Wallpapers",
    tagline: "Cute Panda HD & 4K Wallpapers",
    description: "Enjoy adorable panda wallpapers featuring cute baby pandas, giant pandas eating bamboo, playful panda moments and charming panda photography in stunning 4K resolution.",
    
    // Branding - Black to White gradient
    logo: cdn("app-assets/panda-app/panda-logo.png"),
    primaryColor: "#2F2F2F",
    secondaryColor: "#F5F5F5",
    gradientFrom: "rgba(47,47,47,0.9)",
    gradientTo: "rgba(245,245,245,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.panda",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/panda/thumbnails/5f197f05-817a-4cec-83a8-ef4fae58e2f3.jpg"),
      android: cdn("wallpapers/panda/thumbnails/4089e75a-4c6a-4a15-9301-6082d88e878f.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/panda/thumbnails/6c56bc71-2fa2-4f7e-a518-f0bc4402cfc9.jpg"),
        category: "Baby",
        title: "Adorable Baby Panda",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/panda/thumbnails/f6913a0f-baed-43a5-b5b3-fafe3dc81077.jpg"),
        category: "Bamboo",
        title: "Panda Eating Bamboo",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/panda/thumbnails/97a73bdb-3358-4b26-8243-6734f1205a2a.jpg"),
        category: "Playful",
        title: "Playful Panda Fun",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/panda/thumbnails/a80fdfa7-ced8-4a93-95dd-280eccae9a06.jpg"),
        category: "Sleeping",
        title: "Sleepy Panda Cuteness",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/panda/thumbnails/1b4344f2-bc5f-44b0-aa30-5b3d11e2b5a9.jpg"),
        category: "Giant",
        title: "Giant Panda Portrait",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/panda/thumbnails/4089e75a-4c6a-4a15-9301-6082d88e878f.jpg"),
        category: "Red Panda",
        title: "Red Panda Beauty",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=panda",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=panda",
    }
  },

  "pink": {
    id: "pink",
    name: "Pink 4K Wallpapers",
    tagline: "Beautiful Pink HD & 4K Wallpapers",
    description: "Discover stunning pink wallpapers featuring aesthetic pink themes, blush tones, pink flowers, pastel vibes and beautiful pink photography in gorgeous 4K resolution.",
    
    // Branding - Pink to Rose gradient
    logo: cdn("app-assets/pink-app/pink-logo.png"),
    primaryColor: "#FF69B4",
    secondaryColor: "#FFB6C1",
    gradientFrom: "rgba(255,105,180,0.9)",
    gradientTo: "rgba(255,182,193,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.pink",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/pink/thumbnails/4261a27a-bf34-4ad8-8080-3aef58bbeb8a.jpg"),
      android: cdn("wallpapers/pink/thumbnails/035878c4-08ef-484d-ac0a-92ece98629c4.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/pink/thumbnails/90d561af-1893-4e7e-8d06-4fbf8be2cbbb.jpg"),
        category: "Aesthetic",
        title: "Pink Aesthetic Vibes",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/rose/thumbnails/42021752-19d5-4286-aa58-3d06bfc1a1f4.jpg"),
        category: "Pastel",
        title: "Pastel Pink Dream",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/pink/thumbnails/c5dda533-4acb-4113-be0a-2ed41a562a34.jpg"),
        category: "Flowers",
        title: "Pink Flower Beauty",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/abstract/thumbnails/579c1c3a-7b08-4077-9cc2-584098405f8a.jpg"),
        category: "Abstract",
        title: "Abstract Pink Art",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/beach/thumbnails/31633e02-2cc9-4f4b-95b7-4ffa987dae7f.jpg"),
        category: "Sky",
        title: "Pink Sky Sunset",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/abstract/thumbnails/b930a9ff-83b1-4d5e-a7f5-f1259b066d2f.jpg"),
        category: "Gradient",
        title: "Pink Gradient Flow",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=pink",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=pink",
    }
  },

  "rose": {
    id: "rose",
    name: "Rose 4K Wallpapers",
    tagline: "Beautiful Rose HD & 4K Wallpapers",
    description: "Discover beautiful rose wallpapers featuring red roses, white roses, pink roses, rose gardens and stunning rose photography in romantic 4K resolution.",
    
    // Branding - Red to Pink gradient
    logo: cdn("app-assets/rose-app/rose-logo.png"),
    primaryColor: "#DC143C",
    secondaryColor: "#FF69B4",
    gradientFrom: "rgba(220,20,60,0.9)",
    gradientTo: "rgba(255,105,180,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.rose",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/rose/thumbnails/42021752-19d5-4286-aa58-3d06bfc1a1f4.jpg"),
      android: cdn("wallpapers/rose/thumbnails/feb7444d-0cf4-47f3-a217-f3fda675956f.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/rose/thumbnails/feb7444d-0cf4-47f3-a217-f3fda675956f.jpg"),
        category: "Red Rose",
        title: "Beautiful Red Rose",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/rose/thumbnails/bc6866f3-cd20-4ac5-8a42-c33ef7b0b1df.jpg"),
        category: "White Rose",
        title: "Pure White Rose",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/rose/thumbnails/1df49eff-8829-4134-83e0-5b883c305761.jpg"),
        category: "Pink Rose",
        title: "Lovely Pink Rose",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/rose/thumbnails/71ecc791-4f2e-41f3-8233-cce287f6d803.jpg"),
        category: "Garden",
        title: "Rose Garden Paradise",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/rose/thumbnails/787e3536-0228-41fe-b640-3e08c0a01546.jpg"),
        category: "Bouquet",
        title: "Rose Bouquet Beauty",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/rose/thumbnails/34241279-d0ab-4e6c-b3bd-16aba0007d17.jpg"),
        category: "Romantic",
        title: "Romantic Rose Love",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=rose",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=rose",
    }
  },

  "shiva": {
    id: "shiva",
    name: "Shiva 4K Wallpapers",
    tagline: "Divine Lord Shiva HD & 4K Wallpapers",
    description: "Experience divine spirituality with stunning Lord Shiva wallpapers featuring Mahadev art, Trishul imagery, Shivling, Nataraja and beautiful devotional scenes in 4K resolution.",
    
    // Branding - Blue to Orange gradient
    logo: cdn("app-assets/shiva-app/shiva-logo.png"),
    primaryColor: "#4169E1",
    secondaryColor: "#FF6347",
    gradientFrom: "rgba(65,105,225,0.9)",
    gradientTo: "rgba(255,99,71,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.shiva",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/shiva/thumbnails/c0b7f985-167e-49aa-898a-ff397e9655d8.jpg"),
      android: cdn("wallpapers/instawall/full/7961adb5-ed81-4fca-837a-f884e80428d5.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/shiva/thumbnails/6315f995-ebaf-42ec-b8f4-3840f792700c.png"),
        category: "Mahadev",
        title: "Mahadev Divine Portrait",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/shiva/thumbnails/a5f640ee-2a3a-4b52-b20a-06e2f817cc02.jpg"),
        category: "Trishul",
        title: "Sacred Trishul Symbol",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/shiva/thumbnails/a5eed0ba-cea6-45a9-8a57-436faa9bfdf8.jpg"),
        category: "Nataraja",
        title: "Dancing Nataraja",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/shiva/thumbnails/46455684-63d5-4438-8aa3-1f481dfe18c8.jpg"),
        category: "Meditation",
        title: "Shiva Meditation Pose",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/shiva/thumbnails/c9cf3da4-cff7-4c16-84b1-644f92703de1.png"),
        category: "Rudra",
        title: "Fierce Rudra Avatar",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/shiva/thumbnails/e6c0da2e-f7a6-4ff1-a06e-e3db83d5dd7e.png"),
        category: "Spiritual",
        title: "Spiritual Shiva Art",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=shiva",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=shiva",
    }
  },

  "sky": {
    id: "sky",
    name: "Sky 4K Wallpapers",
    tagline: "Beautiful Sky HD & 4K Wallpapers",
    description: "Discover stunning sky wallpapers featuring blue skies, dramatic clouds, colorful sunsets, starry nights and breathtaking sky photography in brilliant 4K resolution.",
    
    // Branding - Sky Blue to Orange gradient
    logo: cdn("app-assets/sky-app/sky-logo.png"),
    primaryColor: "#87CEEB",
    secondaryColor: "#FF7F50",
    gradientFrom: "rgba(135,206,235,0.9)",
    gradientTo: "rgba(255,127,80,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.sky",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/beach/thumbnails/31633e02-2cc9-4f4b-95b7-4ffa987dae7f.jpg"),
      android: cdn("wallpapers/pink/thumbnails/90d561af-1893-4e7e-8d06-4fbf8be2cbbb.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/pink/thumbnails/90d561af-1893-4e7e-8d06-4fbf8be2cbbb.jpg"),
        category: "Blue Sky",
        title: "Clear Blue Sky Day",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/sky/thumbnails/04249736-c6a4-4537-9264-ab40defc8c05.jpg"),
        category: "Clouds",
        title: "Dramatic Cloud Formation",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/cute-girl/thumbnails/908b109b-b797-4237-9f13-5eabeec30a15.png"),
        category: "Sunset",
        title: "Golden Sky Sunset",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/sky/thumbnails/4ae4412a-9280-4d29-86f3-bccda59f9b53.png"),
        category: "Sunrise",
        title: "Beautiful Sunrise Sky",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/mountains/thumbnails/02946174-431e-4246-b870-d4cb8621728d.jpg"),
        category: "Night Sky",
        title: "Starry Night Sky",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/car/thumbnails/fbdd5271-b421-4823-b8a2-40c4b27652cf.jpg"),
        category: "Rainbow",
        title: "Rainbow Sky Wonder",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=sky",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=sky",
    }
  },

  "snow": {
    id: "snow",
    name: "Snow 4K Wallpapers",
    tagline: "Beautiful Snow HD & 4K Wallpapers",
    description: "Experience winter wonderland with stunning snow wallpapers featuring snowy landscapes, snowflakes, frozen scenes, winter forests and magical snow photography in 4K resolution.",
    
    // Branding - White to Ice Blue gradient
    logo: cdn("app-assets/snow-app/snow-logo.png"),
    primaryColor: "#F5F5F5",
    secondaryColor: "#ADD8E6",
    gradientFrom: "rgba(245,245,245,0.9)",
    gradientTo: "rgba(173,216,230,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.snow",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/christmas/thumbnails/25bc988b-5046-41c4-91e2-05e8818d4b47.png"),
      android: cdn("wallpapers/mountains/thumbnails/802d6bde-396d-411e-a18f-16bf919a6275.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/christmas/thumbnails/4c7b3e53-eba5-4b87-9969-e9e261760a01.png"),
        category: "Snowfall",
        title: "Beautiful Snowfall",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/christmas/thumbnails/105ee49a-7edb-41d1-ba75-ecf73c1b68f8.png"),
        category: "Snowflake",
        title: "Delicate Snowflake",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/nature/thumbnails/ae557ca0-8e24-45cc-95fd-b77b7ab25424.png"),
        category: "Landscape",
        title: "Snowy Winter Landscape",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/winters/thumbnails/c219a295-77ab-40fd-87af-d9497bcb5078.png"),
        category: "Forest",
        title: "Snow-Covered Forest",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/christmas/thumbnails/2890d82c-e72b-457d-ace8-0b72c80d8257.png"),
        category: "Frozen",
        title: "Frozen Winter Scene",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/winters/thumbnails/d44a6d11-0b95-4b55-a824-8deb4a6f6099.png"),
        category: "Mountain",
        title: "Snowy Mountain Peak",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=snow",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=snow",
    }
  },

  "sunrise": {
    id: "sunrise",
    name: "Sunrise 4K Wallpapers",
    tagline: "Beautiful Sunrise HD & 4K Wallpapers",
    description: "Start your day with stunning sunrise wallpapers featuring golden dawn, morning light, beach sunrises, mountain dawns and breathtaking sunrise photography in 4K resolution.",
    
    // Branding - Golden to Orange gradient
    logo: cdn("app-assets/sunrise-app/sunrise-logo.png"),
    primaryColor: "#FFD700",
    secondaryColor: "#FF6347",
    gradientFrom: "rgba(255,215,0,0.9)",
    gradientTo: "rgba(255,99,71,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.sunrise",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/autumn/thumbnails/5b84a522-d171-463f-bc19-2bf67dad731a.jpg"),
      android: cdn("wallpapers/instawall/thumbnails/1b880b31-0777-4be0-b8e3-e2564ddd4fb4.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/fantasy/thumbnails/fb55b19e-71a9-4a1f-858a-8f787fb6cbe0.jpg"),
        category: "Golden",
        title: "Golden Dawn Beauty",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/sunrise/thumbnails/b46e1ead-6960-475f-90a6-5b3670905146.jpg"),
        category: "Beach",
        title: "Beach Sunrise Magic",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/sunrise/thumbnails/c99b38c8-e855-4c62-835b-aff8b64de2ef.jpg"),
        category: "Mountain",
        title: "Mountain Sunrise Glow",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/beach/thumbnails/18b5ebf2-f7fc-4d95-aa3d-afc1be376397.jpg"),
        category: "Ocean",
        title: "Ocean Sunrise View",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/sunrise/thumbnails/8fcf342c-e037-4543-b07c-7171d3ad209b.jpg"),
        category: "Forest",
        title: "Forest Morning Light",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/sunrise/thumbnails/0efdba3c-cccd-41a5-8f66-6eb238e46d9e.jpg"),
        category: "Peaceful",
        title: "Peaceful Morning Sunrise",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=sunrise",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=sunrise",
    }
  },

  "sunset": {
    id: "sunset",
    name: "Sunset 4K Wallpapers",
    tagline: "Beautiful Sunset HD & 4K Wallpapers",
    description: "Experience magical sunsets with stunning wallpapers featuring golden hour, beach sunsets, mountain dusk, city sunsets and breathtaking sunset photography in 4K resolution.",
    
    // Branding - Orange to Purple gradient
    logo: cdn("app-assets/sunset-app/sunset-logo.png"),
    primaryColor: "#FF7F50",
    secondaryColor: "#9370DB",
    gradientFrom: "rgba(255,127,80,0.9)",
    gradientTo: "rgba(147,112,219,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.sunset",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/sky/thumbnails/4ae4412a-9280-4d29-86f3-bccda59f9b53.png"),
      android: cdn("wallpapers/beach/thumbnails/cfd21809-08e7-4f2c-babd-5804dd141bac.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/beach/thumbnails/45c6ee08-77aa-4e93-b8c1-5b23e2270f76.png"),
        category: "Beach",
        title: "Beach Sunset Paradise",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/beach/thumbnails/09331584-0eed-4828-a6f9-402de5ad8b8b.png"),
        category: "Golden Hour",
        title: "Golden Hour Magic",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/mountains/thumbnails/802d6bde-396d-411e-a18f-16bf919a6275.png"),
        category: "Mountain",
        title: "Mountain Sunset View",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/beach/thumbnails/292a94ef-9f50-4f15-b872-bdedc5964bbb.png"),
        category: "City",
        title: "City Skyline Sunset",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/foody/thumbnails/125e549f-ccbe-40cb-a82d-99ff39c81425.png"),
        category: "Ocean",
        title: "Ocean Sunset Horizon",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/couple/thumbnails/15696d72-3236-4c96-b44e-92d254198a62.jpg"),
        category: "Romantic",
        title: "Romantic Sunset Scene",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=sunset",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=sunset",
    }
  },

  "tiger": {
    id: "tiger",
    name: "Tiger 4K Wallpapers",
    tagline: "Majestic Tiger HD & 4K Wallpapers",
    description: "Experience the wild beauty with stunning tiger wallpapers featuring Bengal tigers, white tigers, fierce portraits, jungle scenes and powerful tiger photography in 4K resolution.",
    
    // Branding - Orange to Black gradient
    logo: cdn("app-assets/tiger-app/tiger-logo.png"),
    primaryColor: "#FF8C00",
    secondaryColor: "#1C1C1C",
    gradientFrom: "rgba(255,140,0,0.9)",
    gradientTo: "rgba(28,28,28,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.tiger",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/tiger/thumbnails/d7ad9081-e1c6-48c8-93b4-1a4dd31590e7.jpg"),
      android: cdn("wallpapers/tiger/thumbnails/e63f22c8-ef49-4c6b-a15d-601b16b6d5c9.jpg"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/tiger/thumbnails/2c6daf05-c768-4df4-82bc-fca0fbe4b3e9.jpg"),
        category: "Bengal",
        title: "Majestic Bengal Tiger",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/tiger/thumbnails/68e49c67-9858-4e94-a49b-699c6913ea98.jpg"),
        category: "White Tiger",
        title: "Beautiful White Tiger",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/tiger/thumbnails/f30bdff0-fb42-4873-9bb3-7421aa98a5e9.jpg"),
        category: "Portrait",
        title: "Fierce Tiger Portrait",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/tiger/thumbnails/5744c00b-f51b-4e1d-8992-ec52d68cd955.jpg"),
        category: "Jungle",
        title: "Tiger in the Jungle",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/tiger/thumbnails/a77999a4-d546-40c7-a51d-0f0ec5d17308.jpg"),
        category: "Eyes",
        title: "Tiger Eyes Intensity",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/tiger/thumbnails/1eabc156-1b87-4749-b94d-439c2ee05419.jpg"),
        category: "Wild",
        title: "Wild Tiger Power",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=tiger",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=tiger",
    }
  },

  "diwali": {
    id: "diwali",
    name: "Diwali 4K Wallpapers",
    tagline: "Festive Diwali HD & 4K Wallpapers",
    description: "Celebrate the festival of lights with stunning wallpapers of glowing diyas, colourful rangoli, fireworks, marigold decorations and festive Diwali nights in beautiful 4K resolution.",

    // Branding - Night indigo to diya gold
    logo: cdn("app-assets/diwali-app/diwali-logo.png"),
    primaryColor: "#2E1065",
    secondaryColor: "#FFB020",
    gradientFrom: "rgba(46,16,101,0.9)",
    gradientTo: "rgba(255,176,32,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.diwali",
    appStoreUrl: undefined,

    // Stats
    rating: "4.8",
    downloads: "1K+",
    totalWallpapers: "100+",

    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/diwali/thumbnails/5aa7a5d6-e901-4d5c-bd0f-9406c332802f.jpg"),
      android: cdn("wallpapers/diwali/thumbnails/c84d43d8-5011-4e97-8f5b-226b1d3af7e1.jpg"),
    },

    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),

    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/diwali/thumbnails/5aa7a5d6-e901-4d5c-bd0f-9406c332802f.jpg"),
        category: "Diwali",
        title: "Diya and Fireworks Night",
        likes: 9124
      },
      {
        id: 2,
        image: cdn("wallpapers/diwali/thumbnails/dc652fea-d9eb-41a8-ab0b-32cf2b465078.jpg"),
        category: "Rangoli",
        title: "Diya on Colourful Rangoli",
        likes: 8637
      },
      {
        id: 3,
        image: cdn("wallpapers/diwali/thumbnails/3fda3261-f088-454d-970a-8195044bed6f.jpg"),
        category: "Fireworks",
        title: "Festival of Lights",
        likes: 8402
      },
      {
        id: 4,
        image: cdn("wallpapers/ram/thumbnails/fc8ec639-f3a8-4f20-9c46-3091f167e525.jpg"),
        category: "Devotional",
        title: "Shri Ram Silhouette",
        likes: 7918
      },
      {
        id: 5,
        image: cdn("wallpapers/hanuman/thumbnails/fa56edbb-5c83-440c-966b-f04daf13b9a9.jpg"),
        category: "Devotional",
        title: "Hanuman Ji with Gada",
        likes: 7541
      },
      {
        id: 6,
        image: cdn("wallpapers/diwali/thumbnails/191b5fba-9623-4136-ae96-24e84b131537.jpg"),
        category: "Diya",
        title: "Traditional Diya Lamp",
        likes: 7233
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=diwali",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=diwali",
    }
  },

  "navratri": {
    id: "navratri",
    name: "Navratri 4K Wallpapers",
    tagline: "Festive Navratri HD & 4K Wallpapers",
    description: "Celebrate Navratri with stunning wallpapers of Maa Durga, garba nights, glowing diyas, colourful rangoli and festive marigolds in beautiful 4K resolution.",

    // Branding - Maroon to Marigold gradient
    logo: cdn("app-assets/navratri-app/navratri-logo.png"),
    primaryColor: "#8E0E20",
    secondaryColor: "#F5A623",
    gradientFrom: "rgba(142,14,32,0.9)",
    gradientTo: "rgba(245,166,35,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.navratri",
    appStoreUrl: undefined,

    // Stats
    rating: "4.8",
    downloads: "1K+",
    totalWallpapers: "100+",

    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/navratri/thumbnails/5d5b9f2b-10aa-4750-a210-9c16c95774c0.jpg"),
      android: cdn("wallpapers/navratri/thumbnails/8fbae17b-a545-40cd-a45a-83721acd08ad.jpg"),
    },

    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),

    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/navratri/thumbnails/5d5b9f2b-10aa-4750-a210-9c16c95774c0.jpg"),
        category: "Durga",
        title: "Maa Durga on Lion",
        likes: 9124
      },
      {
        id: 2,
        image: cdn("wallpapers/navratri/thumbnails/8fbae17b-a545-40cd-a45a-83721acd08ad.jpg"),
        category: "Durga",
        title: "Mahishasura Mardini",
        likes: 8637
      },
      {
        id: 3,
        image: cdn("wallpapers/navratri/thumbnails/b83421f5-ab50-420d-aefc-bd555f023115.jpg"),
        category: "Devotional",
        title: "Serene Maa Durga",
        likes: 8402
      },
      {
        id: 4,
        image: cdn("wallpapers/navratri/thumbnails/e1dea037-8dcf-4a07-83aa-ec1d15f240d7.jpg"),
        category: "Festival",
        title: "Durga Puja Pandal",
        likes: 7918
      },
      {
        id: 5,
        image: cdn("wallpapers/navratri/thumbnails/d09f6b48-e19c-4494-8654-e48c9e7a140a.jpg"),
        category: "Navadurga",
        title: "Nine Forms of Durga",
        likes: 7541
      },
      {
        id: 6,
        image: cdn("wallpapers/navratri/thumbnails/94140823-663c-4805-894c-f401ea0fc4fa.jpg"),
        category: "Rangoli",
        title: "Festive Rangoli",
        likes: 7233
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=navratri",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=navratri",
    }
  },

  "durga-mata": {
    id: "durga-mata",
    name: "Maa Durga 4K Wallpapers",
    tagline: "Divine Maa Durga HD & 4K Wallpapers",
    description: "Seek blessings with stunning Maa Durga wallpapers featuring the Devi on her lion, temple idols, glowing diyas, marigold offerings and divine Navdurga art in beautiful 4K resolution.",

    // Branding - Deep Red to Gold gradient
    logo: cdn("app-assets/durga-mata-app/durga-mata-logo.png"),
    primaryColor: "#9B1B30",
    secondaryColor: "#E0A64B",
    gradientFrom: "rgba(155,27,48,0.9)",
    gradientTo: "rgba(224,166,75,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.durgamata",
    appStoreUrl: undefined,

    // Stats
    rating: "4.8",
    downloads: "1K+",
    totalWallpapers: "100+",

    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/durga-mata/thumbnails/8129c310-0561-44ee-ac2b-33d9a2463ba1.jpg"),
      android: cdn("wallpapers/durga-mata/thumbnails/6b91b08b-8e72-4474-85f5-cd36f21b23aa.jpg"),
    },

    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),

    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/durga-mata/thumbnails/50a1062d-bd72-4b9d-b23f-73126fd8a560.jpg"),
        category: "Durga",
        title: "Devi and the White Tiger",
        likes: 9218
      },
      {
        id: 2,
        image: cdn("wallpapers/durga-mata/thumbnails/fe5fba19-7482-4ea5-afb6-175cc137b972.jpg"),
        category: "Temple",
        title: "Golden Temple Idol",
        likes: 8734
      },
      {
        id: 3,
        image: cdn("wallpapers/durga-mata/thumbnails/9a82cbee-de2c-408e-be24-49224f67dc15.jpg"),
        category: "Devotional",
        title: "Lotus and Diya Blessing",
        likes: 8391
      },
      {
        id: 4,
        image: cdn("wallpapers/durga-mata/thumbnails/54450dda-a1e6-487c-8613-b30a8e0d6feb.jpg"),
        category: "Navratri",
        title: "Maa Durga on Her Tiger",
        likes: 8046
      },
      {
        id: 5,
        image: cdn("wallpapers/durga-mata/thumbnails/bbe54122-b562-4d9d-bfef-7f80c16ecf7b.jpg"),
        category: "Shiva Parivar",
        title: "Divine Family",
        likes: 7702
      },
      {
        id: 6,
        image: cdn("wallpapers/durga-mata/thumbnails/c8b686d7-6ecc-4c71-95c4-89152d1c45df.jpg"),
        category: "Devotional",
        title: "Moonlit Devi",
        likes: 7355
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=durga-mata",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=durga-mata",
    }
  },

  "hanuman": {
    id: "hanuman",
    name: "Hanuman 4K Wallpapers",
    tagline: "Bajrangbali HD & 4K Wallpapers",
    description: "Bajrangbali wallpapers in stunning 4K - Panchmukhi darshan, Lanka Dahan, the Sanjeevani mountain, temple idols, Ram bhakti art and powerful HD portraits of Hanuman.",

    // Branding - Sindoor Orange to Gold gradient
    logo: cdn("app-assets/hanuman-app/hanuman-logo.png"),
    primaryColor: "#C1440E",
    secondaryColor: "#F2A413",
    gradientFrom: "rgba(193,68,14,0.9)",
    gradientTo: "rgba(242,164,19,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.hanuman",
    appStoreUrl: undefined,

    // Stats
    rating: "4.8",
    downloads: "1K+",
    totalWallpapers: "150+",

    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/hanuman/thumbnails/9c47a4eb-13de-434b-a276-a77990d54a50.jpg"),
      android: cdn("wallpapers/hanuman/thumbnails/ee3f6fc1-f22d-4816-acd8-82bb05dc291e.jpg"),
    },

    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),

    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/hanuman/thumbnails/a8d5bbff-d727-4f86-a440-aba9e55575b4.jpg"),
        category: "Devotional",
        title: "Panchmukhi Darshan",
        likes: 9412
      },
      {
        id: 2,
        image: cdn("wallpapers/hanuman/thumbnails/e487408a-bdae-4acf-af65-9526792dfcaf.jpg"),
        category: "Ramayana",
        title: "Lanka Dahan",
        likes: 8967
      },
      {
        id: 3,
        image: cdn("wallpapers/hanuman/thumbnails/02c8a2a1-45ed-41ff-9b57-36fc9d4a5901.jpg"),
        category: "Ramayana",
        title: "Carrying Ram and Lakshman",
        likes: 8544
      },
      {
        id: 4,
        image: cdn("wallpapers/hanuman/thumbnails/7151b1ba-8d97-47e1-87c5-3eea16ad9708.jpg"),
        category: "Minimal",
        title: "The Sanjeevani Mountain",
        likes: 8190
      },
      {
        id: 5,
        image: cdn("wallpapers/hanuman/thumbnails/67ed3280-c7ce-4316-a324-2ab4f75ff6e4.jpg"),
        category: "Temple",
        title: "Temple Idol at Dusk",
        likes: 7823
      },
      {
        id: 6,
        image: cdn("wallpapers/hanuman/thumbnails/af1bffbf-94bd-49fb-869e-fe4fe4817bf8.jpg"),
        category: "Dark",
        title: "Storm Over the Mountain",
        likes: 7461
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=hanuman",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=hanuman",
    }
  },
  "ram": {
    id: "ram",
    name: "Shri Ram 4K Wallpapers",
    tagline: "Jai Shree Ram HD & 4K Wallpapers",
    description: "Shri Ram wallpapers in stunning 4K - Ram Lalla darshan at Ayodhya, Ram Darbar with Sita, Lakshman and Hanuman, dhanush and baan warrior art, Jai Shree Ram typography and devotional HD portraits.",

    // Branding - Ember Brown to Glow Gold gradient
    logo: cdn("app-assets/ram-app/ram-logo.png"),
    primaryColor: "#8C3A0F",
    secondaryColor: "#F5C542",
    gradientFrom: "rgba(140,58,15,0.9)",
    gradientTo: "rgba(245,197,66,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.ram",
    appStoreUrl: undefined,

    // Stats
    rating: "4.8",
    downloads: "1K+",
    totalWallpapers: "150+",

    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/ram/thumbnails/35729809-55eb-48e9-bf50-a5156768fb9b.jpg"),
      android: cdn("wallpapers/ram/thumbnails/3930453d-30f6-40a6-89fa-621693ff9bc7.jpg"),
    },

    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),

    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/ram/thumbnails/ebaa8ed6-78e4-46d3-96b8-5aed3dfd1b31.jpg"),
        category: "Warrior",
        title: "Maryada Purushottam",
        likes: 9124
      },
      {
        id: 2,
        image: cdn("wallpapers/ram/thumbnails/bdb9ca34-ab28-4532-b5ad-02f629fce095.jpg"),
        category: "Darshan",
        title: "Ram Lalla Darshan",
        likes: 8871
      },
      {
        id: 3,
        image: cdn("wallpapers/ram/thumbnails/d1b6ac73-7d0b-4fcb-9654-754f28981fb2.jpg"),
        category: "Ramayana",
        title: "Ram and Hanuman",
        likes: 8503
      },
      {
        id: 4,
        image: cdn("wallpapers/ram/thumbnails/44ec587f-a8eb-4321-ae0a-8aa7fb86c73d.jpg"),
        category: "Ayodhya",
        title: "Return to Ayodhya",
        likes: 8194
      },
      {
        id: 5,
        image: cdn("wallpapers/ram/thumbnails/3a009d70-da2a-46aa-b712-7e5288eac442.jpg"),
        category: "Cinematic",
        title: "Storm Over the Waters",
        likes: 7768
      },
      {
        id: 6,
        image: cdn("wallpapers/ram/thumbnails/5d692e9c-f4e5-4fd7-9a40-f9000bf590d5.jpg"),
        category: "Minimal",
        title: "Jai Shree Ram",
        likes: 7412
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=ram",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=ram",
    }
  },
  "halloween": {
    id: "halloween",
    name: "Halloween 4K Wallpapers",
    tagline: "Spooky HD & 4K Wallpapers for October",
    description: "Halloween wallpapers in stunning 4K - glowing jack-o'-lanterns, cute ghosts and pumpkins, haunted houses and foggy graveyards, witches, black cats and spooky night scenes for October.",

    // Branding - Pumpkin Orange to Midnight Purple gradient
    logo: cdn("app-assets/halloween-app/halloween-logo.png"),
    primaryColor: "#F26B1D",
    secondaryColor: "#3B1E5F",
    gradientFrom: "rgba(242,107,29,0.9)",
    gradientTo: "rgba(59,30,95,0.9)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.halloween",
    appStoreUrl: undefined,

    // Stats
    rating: "4.8",
    downloads: "1K+",
    totalWallpapers: "100+",

    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/halloween/thumbnails/82225f73-694b-4393-bdbf-63b60e26e884.jpg"),
      android: cdn("wallpapers/halloween/thumbnails/fe66c179-f571-4106-88d6-214377f7d07f.jpg"),
    },

    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),

    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      { id: 1, image: cdn("wallpapers/halloween/thumbnails/e92496aa-c23e-458d-a66a-7dacbf00343e.jpg"), category: "Cute", title: "Little Ghost", likes: 9210 },
      { id: 2, image: cdn("wallpapers/halloween/thumbnails/68c40003-66b0-4df2-93b2-88a2eab83062.jpg"), category: "Night", title: "Owl on the Lantern", likes: 8944 },
      { id: 3, image: cdn("wallpapers/halloween/thumbnails/a164ca93-b3fe-47f1-8887-be0dbe4cc681.jpg"), category: "Haunted", title: "The Old Mansion", likes: 8617 },
      { id: 4, image: cdn("wallpapers/halloween/thumbnails/99ec3e4d-d927-411b-a7c7-b29725b9d502.jpg"), category: "Witch", title: "Broom Ride", likes: 8288 },
      { id: 5, image: cdn("wallpapers/halloween/thumbnails/25e56345-282d-4a85-941d-d23b725b66ae.jpg"), category: "Pumpkin", title: "Jack-o'-Lantern", likes: 7955 },
      { id: 6, image: cdn("wallpapers/halloween/thumbnails/763dc873-57a0-40f8-b6c1-01f996bc5714.jpg"), category: "Minimal", title: "Boo", likes: 7603 }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=halloween",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=halloween",
    }
  },
  "usarmy": {
    id: "usarmy",
    name: "US Army & Military Wallpaper",
    tagline: "US Army & Military HD & 4K Wallpapers",
    description: "US Army and military wallpapers in stunning 4K - soldiers and special forces, US Army insignia and dog tags, fighter jets and stealth bombers, aircraft carriers and warships, camo patterns and the Stars and Stripes.",

    // Branding - Olive Drab to Gunmetal gradient
    logo: cdn("app-assets/usarmy-app/usarmy-logo.png"),
    primaryColor: "#4B5320",
    secondaryColor: "#2A3439",
    gradientFrom: "rgba(75,83,32,0.9)",
    gradientTo: "rgba(42,52,57,0.9)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.usarmy",
    appStoreUrl: undefined,

    // Stats
    rating: "4.8",
    downloads: "1K+",
    totalWallpapers: "90+",

    // Hero Section
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/us-army/thumbnails/a92a7cfa-1c6b-4b07-b08c-64284fba4d1a.jpg"),
      android: cdn("wallpapers/us-army/thumbnails/72fd3226-ba68-4197-a9cf-c5736a319208.jpg"),
    },

    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),

    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      { id: 1, image: cdn("wallpapers/us-army/thumbnails/c25398af-3208-41e6-b60b-6ddc416745d7.jpg"), category: "Emblem", title: "Army Ranger", likes: 9180 },
      { id: 2, image: cdn("wallpapers/us-army/thumbnails/2dd72f38-1a6e-4f23-ac57-e14c28aa04ed.jpg"), category: "Aviation", title: "F-35 Takeoff", likes: 8905 },
      { id: 3, image: cdn("wallpapers/us-army/thumbnails/d3ea7065-27ba-48e0-920e-9cac35126319.jpg"), category: "Navy", title: "Carrier Strike Group", likes: 8640 },
      { id: 4, image: cdn("wallpapers/us-army/thumbnails/c7e7716b-9a83-460c-ac5b-4cb08fe5ddc6.jpg"), category: "Soldier", title: "Into the Dust", likes: 8312 },
      { id: 5, image: cdn("wallpapers/us-army/thumbnails/925cc0ac-be1b-43bf-8ce0-83195e1ef268.jpg"), category: "Camo", title: "Blackout Camo", likes: 7988 },
      { id: 6, image: cdn("wallpapers/us-army/thumbnails/537ed88d-f27c-4827-95ff-54b3d0732f67.jpg"), category: "Patriotic", title: "Stars and Stripes", likes: 7640 }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=usarmy",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=usarmy",
    }
  },
  "winters": {
    id: "winters",
    name: "Winters 4K Wallpapers",
    tagline: "Cozy Winter HD & 4K Wallpapers",
    description: "Embrace the winter season with stunning wallpapers featuring snowy landscapes, cozy scenes, winter forests, frozen lakes and beautiful winter photography in 4K resolution.",
    
    // Branding - Ice Blue to White gradient
    logo: cdn("app-assets/winters-app/winters-logo.png"),
    primaryColor: "#ADD8E6",
    secondaryColor: "#F0F8FF",
    gradientFrom: "rgba(173,216,230,0.9)",
    gradientTo: "rgba(240,248,255,0.85)",

    // Download Links
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.kreviastudios.winters",
    appStoreUrl: undefined,
    
    // Stats
    rating: "4.8",
    downloads: "100K+",
    totalWallpapers: "3K+",
    
    // Hero Section - ALL images from CloudFront
    heroBackgroundImage: cdn("app-assets/instwall-app/hero-background-instawall.jpg"),
    heroImages: {
      iphone: cdn("wallpapers/christmas/thumbnails/25bc988b-5046-41c4-91e2-05e8818d4b47.png"),
      android: cdn("wallpapers/mountains/thumbnails/802d6bde-396d-411e-a18f-16bf919a6275.png"),
    },
    
    // Download Section
    downloadBackgroundImage: cdn("app-assets/instwall-app/bottom-hero-banner.jpeg"),
    
    // Gallery - 6 Featured Wallpapers
    galleryWallpapers: [
      {
        id: 1,
        image: cdn("wallpapers/christmas/thumbnails/4c7b3e53-eba5-4b87-9969-e9e261760a01.png"),
        category: "Snowfall",
        title: "Beautiful Snowfall",
        likes: 8421
      },
      {
        id: 2,
        image: cdn("wallpapers/christmas/thumbnails/105ee49a-7edb-41d1-ba75-ecf73c1b68f8.png"),
        category: "Snowflake",
        title: "Delicate Snowflake",
        likes: 7654
      },
      {
        id: 3,
        image: cdn("wallpapers/nature/thumbnails/ae557ca0-8e24-45cc-95fd-b77b7ab25424.png"),
        category: "Landscape",
        title: "Snowy Winter Landscape",
        likes: 9032
      },
      {
        id: 4,
        image: cdn("wallpapers/winters/thumbnails/c219a295-77ab-40fd-87af-d9497bcb5078.png"),
        category: "Forest",
        title: "Snow-Covered Forest",
        likes: 8217
      },
      {
        id: 5,
        image: cdn("wallpapers/christmas/thumbnails/2890d82c-e72b-457d-ace8-0b72c80d8257.png"),
        category: "Frozen",
        title: "Frozen Winter Scene",
        likes: 7345
      },
      {
        id: 6,
        image: cdn("wallpapers/winters/thumbnails/d44a6d11-0b95-4b55-a824-8deb4a6f6099.png"),
        category: "Mountain",
        title: "Snowy Mountain Peak",
        likes: 8796
      }
    ],

    // Legal Links
    legalLinks: {
      privacyPolicy: "https://instawall.in/legal/privacy-policy?app=winters",
      termsOfService: "https://instawall.in/legal/terms-of-service?app=winters",
    }
  },
  
  // Add more apps here as needed
};

/**
 * Get app configuration by ID
 * @param appId - The app identifier from URL query parameter
 * @returns AppWebConfig or null if not found
 */
export function getAppWebConfig(appId: string | null): AppWebConfig | null {
  if (!appId) {
    // Default to "instawall" if no app specified
    return APPS_WEB_CONFIG["instawall"];
  }
  
  const normalizedId = appId.toLowerCase().trim();
  return APPS_WEB_CONFIG[normalizedId] || null;
}

/**
 * Get all app IDs
 * @returns Array of all registered app IDs
 */
export function getAllAppWebIds(): string[] {
  return Object.keys(APPS_WEB_CONFIG);
}

/**
 * Check if an app ID is valid
 * @param appId - The app identifier to check
 * @returns True if the app exists in the registry
 */
export function isValidAppWebId(appId: string): boolean {
  const normalizedId = appId.toLowerCase().trim();
  return normalizedId in APPS_WEB_CONFIG;
}
